// SPDX-License-Identifier: Apache-2.0 AND LicenseRef-Pachamama-Proprietary
// Copyright 2026 The Zyra Project
// Modifications copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Browse UI — dataset discovery panel with multi-select chip
 * filters, search, sort, and card rendering.
 *
 * Phase 4 §6.1 + §6.5 of `docs/WEB_CATALOG_FEATURES_PLAN.md`.
 * Replaces the previous single-select category bar with typed
 * facet groups (Category & content / Format & medium / Time /
 * Quality & availability), and routes every visibility decision
 * through the pure {@link filterDatasets} engine so the
 * forthcoming Graph / Timeline / Map view modes (§6.7–§6.9)
 * share one source of truth.
 */

import type { Dataset } from '../types'
import { isZipDownloadable } from '../services/downloadService'
import { openDownloadDialog } from './downloadDialogUI'
import { toggleHelp } from './helpUI'
import { attachDatasetMenu, openDatasetMenu, type DatasetMenuActions } from './datasetMenu'
import { openPlans, openSignIn } from './xrPlansUI'
import { mountLessonsTab } from './lessonsTab'
import { mountMyDataTab, myDataTabVisible, ownDataAllowed } from './myDataTab'
import { buildSmartIndex, smartSearch } from '../services/smartSearch'
import { SemanticSearch, type SemanticHit } from '../services/semanticSearch'
import { accountState, currentEntitlements, onAccountChange } from '../services/account'
import { educationStreamsAllowed, effectivePackages, onLentPackagesChange } from '../services/planAccess'
import { twinHiddenIds } from '../services/libraryTwins'
import { escapeHtml, escapeAttr } from './domUtils'
import { emit, startDwell, hashQuery, type DwellHandle } from '../analytics'
import { plural, t, type MessageKey } from '../i18n'
import { DATA_KINDS, THEMES, THEME_LABEL, dataKindOf, displayTitle, isEducationOnly, outsidePlan, packageOf, themesOf, type DataKind, type StreamPackage, type Theme } from '../services/datasetTaxonomy'
import { formatDate, formatNumber } from '../i18n/format'
import { parseIsoDurationMs } from '../utils/frames'
import { addedDay } from '../utils/time'
import { axisEndMs, createFrameAxis, lastFrameTimeMs } from '../services/frameCalendar'
import { renderMarkdown } from '../services/markdownRenderer'
import { isSosCatalogPage } from '../services/lessonLibrary'
import {
  BASELINE_RESOLVERS,
  PERIOD_RESOLVER,
  extractYear,
  filterDatasets,
  hasModelYearCoverage,
  makeRecentlyViewedResolver,
  mergeFilterStates,
  parseSearchQuery,
  setFacet,
  toggleBooleanFacet,
  toggleFacet,
  type FacetPredicate,
  type FilterState,
  type FormatBucket,
} from '../services/datasetFilter'
import {
  applyFilterStateToUrl,
  readFilterStateFromUrl,
} from '../utils/catalogFilters'
import {
  clearVisits,
  countNewSince,
  getLastSession,
  getRecent,
  getVisitedIds,
  onVisitsChange,
  VISITS_LRU_CAP,
} from '../services/visitMemory'
import { getCatalogMode } from '../utils/catalogMode'
import type { CatalogGraphController } from './catalogGraphUI'
import type { CatalogTimelineController } from './catalogTimelineUI'
import type { CatalogMapController } from './catalogMapUI'
import { regionSlugFor } from '../services/datasetCoverage'

/** AYNI: names for the stream host's region slugs, for a region filter's chip. */
const REGION_NAME_KEYS = {
  spain: 'browse.region.spain',
  conus: 'browse.region.conus',
  netherlands: 'browse.region.netherlands',
  europe: 'browse.region.europe',
  'north-america': 'browse.region.northAmerica',
} as const satisfies Record<string, MessageKey>

/** Tier B dwell handle for the browse overlay — non-null while the
 * overlay is visible. Started on showBrowseUI when the overlay
 * transitions from hidden, stopped on hideBrowseUI / collapseBrowseUI.
 * Tier-gated at emit time so the wiring is unconditional. */
let browseDwellHandle: DwellHandle | null = null

/** Bucket a result count for the `browse_filter.result_count_bucket`
 * schema — we want coarse buckets so the telemetry can't be used to
 * fingerprint a specific search's uniqueness. */
function bucketResultCount(n: number): '0' | '1-10' | '11-50' | '50+' {
  if (n <= 0) return '0'
  if (n <= 10) return '1-10'
  if (n <= 50) return '11-50'
  return '50+'
}

// Re-export so existing callers (chatUI, datasetLoader)
// continue to import these from browseUI.
export { escapeHtml, escapeAttr }

/**
 * §9.2 — light (or clear) the "new since your last visit" badge on
 * the Browse trigger button. The count is catalog entries whose
 * `enriched.dateAdded` is later than the previous session-end
 * timestamp; {@link countNewSince} fails closed (0 on a first-ever
 * visit, or for rows without a parseable `dateAdded`), so the badge
 * never produces false positives.
 *
 * Idempotent and DOM-defensive: a missing Browse button is a no-op
 * (the tools menu mounts after the catalog loads, but call order is
 * not guaranteed). Re-running with count 0 removes any existing
 * badge so the affordance doesn't go stale once the user returns.
 */
export function refreshBrowseNewSinceBadge(datasets: readonly Dataset[]): void {
  const btn = document.getElementById('tools-menu-browse')
  if (!btn) return
  const count = countNewSince(datasets, getLastSession())
  const existing = btn.querySelector('.browse-new-badge')
  // The default accessible name for the Browse trigger — restored
  // whenever the badge clears so the button can't keep announcing a
  // stale "{n} new since your last visit" from a prior non-zero run.
  const defaultLabel = t('tools.browse.aria')
  if (count <= 0) {
    existing?.remove()
    btn.removeAttribute('data-new-since')
    btn.setAttribute('aria-label', defaultLabel)
    btn.setAttribute('title', defaultLabel)
    return
  }
  const label = t('browse.newSince.badge', { count: formatNumber(count) })
  let badge = existing as HTMLElement | null
  if (!badge) {
    badge = document.createElement('span')
    badge.className = 'browse-new-badge'
    badge.setAttribute('aria-hidden', 'true')
    btn.appendChild(badge)
  }
  badge.textContent = formatNumber(count)
  btn.setAttribute('data-new-since', String(count))
  // The numeric badge is aria-hidden (decorative); surface the count
  // to assistive tech via the button's accessible name instead.
  btn.setAttribute('aria-label', label)
  btn.setAttribute('title', label)
}

// --- Browse UI constants ---
const CARD_DESCRIPTION_MAX_LENGTH = 120
const MAX_CARD_CATEGORIES = 3
const MAX_CARD_KEYWORDS = 12

/** AYNI: how long typing must pause before meaning search is asked. */
const SEMANTIC_DELAY_MS = 350

// ---------------------------------------------------------------------------
// AYNI: tabs — Catalog, Recent (owner, 2026-10-03); Khipus (was Lessons, 2026-10-05);
// My data (own data, 2026-10-06). The Playlists tab went with playlists (owner,
// 2026-11-03): Khipus replace them, and a tab remembered as 'playlists' opens Khipus.
// ---------------------------------------------------------------------------

export type BrowseTab = 'catalog' | 'recent' | 'lessons' | 'mydata'
const BROWSE_TABS: readonly BrowseTab[] = ['catalog', 'recent', 'lessons', 'mydata']

/**
 * AYNI — own data: My data is a tab for a plan with own data, and (phase
 * 4) for any signed-in account, which may have datasets shared with it
 * ("Shared with me"; uploading stays locked, shown with the lock). Signed
 * out it is a locked button that opens the plans; a class borrowing a
 * teacher's tools does not see it at all.
 */
function tabOpen(tab: BrowseTab): boolean {
  return tab !== 'mydata' || (myDataTabVisible() && (ownDataAllowed() || accountState().status === 'signedIn'))
}

/** Show or hide My data's button and its lock, as the account and any lent tools say. */
export function syncMyDataTab(): void {
  const btn = document.getElementById('browse-tab-mydata')
  if (!btn) return
  const visible = myDataTabVisible()
  btn.classList.toggle('hidden', !visible)
  btn.hidden = !visible
  const locked = visible && !ownDataAllowed()
  btn.classList.toggle('browse-tab-locked', locked)
  btn.querySelector('.browse-tab-lock')?.classList.toggle('hidden', !locked)
  btn.setAttribute('aria-label', locked ? t('browse.tab.myData.locked') : t('browse.tab.myData'))
  if (activeTab === 'mydata' && !tabOpen('mydata')) setBrowseTab('catalog')
}
const TAB_STORAGE_KEY = 'ayni-browse-tab.v1'

/** The tab a remembered value names: Khipus for the retired Playlists tab, the catalogue for anything else unknown. */
export function browseTabFrom(raw: string | null): BrowseTab {
  if (raw === 'playlists') return 'lessons'
  return BROWSE_TABS.includes(raw as BrowseTab) ? (raw as BrowseTab) : 'catalog'
}

function loadTab(): BrowseTab {
  try {
    return browseTabFrom(window.localStorage.getItem(TAB_STORAGE_KEY))
  } catch {
    return 'catalog'
  }
}

let activeTab: BrowseTab = typeof window === 'undefined' ? 'catalog' : loadTab()
/** Set by `showBrowseUI`: renders a tab's content when it is shown. */
let onTabShown: ((tab: BrowseTab) => void) | null = null

// --- AYNI (multi-globe plan, phase 0): loading into another globe -------

/** Whether a card's dataset shares dates with globe 1 (the main app answers); null while unknown or when nothing is loaded there. */
let compatibilityOf: ((id: string) => 'yes' | 'no' | null) | null = null
/** Set by `showBrowseUI`: redraw the cards (their compatibility marks changed). */
let rerenderCards: (() => void) | null = null

export interface BrowseTargetBanner {
  /** "Loading into globe 2". */
  label: string
  cancel: string
  cancelAria: string
  onCancel(): void
}

/**
 * Say which globe the next dataset goes to, in a banner under the
 * header; null takes it away. Shown when a globe's chip opened Browse.
 */
export function setBrowseTarget(banner: BrowseTargetBanner | null): void {
  const inner = document.getElementById('browse-inner')
  let el = document.getElementById('browse-target')
  if (!banner) {
    el?.remove()
    return
  }
  if (!inner) return
  if (!el) {
    el = document.createElement('div')
    el.id = 'browse-target'
    el.className = 'browse-target'
    el.setAttribute('role', 'status')
    const header = document.getElementById('browse-header')
    if (header?.parentElement === inner) header.insertAdjacentElement('afterend', el)
    else inner.prepend(el)
  }
  el.innerHTML = `<span class="browse-target-text">${escapeHtml(banner.label)}</span>`
    + `<button type="button" class="browse-target-cancel" id="browse-target-cancel" aria-label="${escapeAttr(banner.cancelAria)}">${escapeHtml(banner.cancel)}</button>`
  el.querySelector('#browse-target-cancel')?.addEventListener('click', (e) => {
    e.stopPropagation()
    banner.onCancel()
  })
}

/** Mark each card by whether it shares dates with globe 1 (while loading into another globe); null clears the marks. */
export function setBrowseCompatibility(fn: ((id: string) => 'yes' | 'no' | null) | null): void {
  compatibilityOf = fn
  rerenderCards?.()
}

/** The dates of some cards' datasets arrived: redraw the marks, if any are shown. */
/** Draws the cards again (the satellites index arrived after Browse opened). */
export function refreshBrowseCards(): void {
  rerenderCards?.()
}

export function refreshBrowseCompatibility(): void {
  if (compatibilityOf) rerenderCards?.()
}

/** A card's compatibility mark, when loading into another globe. */
function renderCompatibility(d: Dataset): string {
  const verdict = compatibilityOf?.(d.id) ?? null
  if (!verdict) return ''
  const key = verdict === 'yes' ? 'browse.target.compatible' : 'browse.target.incompatible'
  return `<span class="browse-badge browse-badge-compat" data-compat="${verdict}">${escapeHtml(t(key))}</span>`
}

export function getBrowseTab(): BrowseTab {
  return activeTab
}

/**
 * Show one of the browse panel's tabs. Remembered for the next visit.
 * `focus` moves the keyboard to the tab (a keyboard switch).
 */
export function setBrowseTab(tab: BrowseTab, focus = false): void {
  if (!tabOpen(tab)) tab = 'catalog'
  activeTab = tab
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab)
  } catch {
    /* ignore */
  }
  for (const name of BROWSE_TABS) {
    const btn = document.getElementById(`browse-tab-${name}`)
    const pane = document.getElementById(`browse-pane-${name}`)
    const on = name === tab
    btn?.setAttribute('aria-selected', String(on))
    btn?.setAttribute('tabindex', on ? '0' : '-1')
    btn?.classList.toggle('active', on)
    pane?.classList.toggle('hidden', !on)
    if (on && focus) btn?.focus()
  }
  document.getElementById('browse-overlay')?.setAttribute('data-tab', tab)
  onTabShown?.(tab)
}

/** Wire the tab buttons once: click, and arrow keys between them. */
function wireTabs(): void {
  const list = document.getElementById('browse-tabs')
  if (!list || list.dataset.wired) return
  list.dataset.wired = 'true'
  list.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-tab]')
    if (!btn) return
    // AYNI: My data without own data in the plan opens the plans instead.
    if (btn.dataset.tab === 'mydata' && !tabOpen('mydata')) {
      openPlans(btn, 'research', { notice: t('plans.locked.ownData') })
      return
    }
    setBrowseTab(btn.dataset.tab as BrowseTab)
  })
  list.addEventListener('keydown', (e) => {
    // Arrow keys move between the tabs that open (not a hidden or locked one).
    const tabs = BROWSE_TABS.filter(tabOpen)
    const i = Math.max(0, tabs.indexOf(activeTab))
    const rtl = document.documentElement.dir === 'rtl'
    let next = -1
    if (e.key === 'ArrowRight') next = rtl ? i - 1 : i + 1
    else if (e.key === 'ArrowLeft') next = rtl ? i + 1 : i - 1
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = tabs.length - 1
    else return
    e.preventDefault()
    setBrowseTab(tabs[(next + tabs.length) % tabs.length], true)
  })
}

// AYNI: small icons for the view switch, so it fits beside the Filters button.
const VIEW_ICONS: Readonly<Record<ViewMode, string>> = {
  cards: '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1 2h14v3H1zM1 6.5h14v3H1zM1 11h14v3H1z"/></svg>',
  graph: '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><g fill="none" stroke="currentColor" stroke-width="1.4"><path d="M4 4l8 3M4 4l2 8M12 7l-6 5"/></g><g fill="currentColor"><circle cx="4" cy="4" r="2"/><circle cx="12" cy="7" r="2"/><circle cx="6" cy="12" r="2"/></g></svg>',
  timeline: '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1 2.5h8v2.5H1zM5 6.75h10v2.5H5zM3 11h7v2.5H3z"/></svg>',
  map: '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><g fill="none" stroke="currentColor" stroke-width="1.3"><circle cx="8" cy="8" r="6.3"/><path d="M1.7 8h12.6M8 1.7c2.2 2.4 2.2 10.2 0 12.6M8 1.7c-2.2 2.4-2.2 10.2 0 12.6"/></g></svg>',
}

/** The fixed order of format buckets in the Format chip group. Other
 *  comes last because it's the catch-all. */
const FORMAT_BUCKETS: readonly FormatBucket[] = ['video', 'image', 'story', 'other']

/**
 * Catalog view-mode (Phase 4 §6.7+). Cards is the baseline grid,
 * Graph is the §6.7 network view, Timeline is the §6.8 horizontal
 * coverage view, Map is the §6.9 bounding-box view (AYNI: Equal Earth). All
 * four are rendered as buttons in the segmented control.
 */
type ViewMode = 'cards' | 'graph' | 'timeline' | 'map'

/** localStorage key holding the user's selected view mode.
 *  Versioned so a future schema change can ignore stale shapes. */
const VIEW_MODE_STORAGE_KEY = 'sos-browse-view-mode.v1'

/**
 * Read the persisted view mode, falling back to `'cards'`. SSR-safe.
 *
 * All four view-modes — `'cards'`, `'graph'`, `'timeline'`, `'map'`
 * — are accepted from storage. Unknown modes normalise to `'cards'`
 * so the toggle's active state is always meaningful.
 */
function loadViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'cards'
  try {
    const raw = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    if (raw === 'graph') return 'graph'
    if (raw === 'timeline') return 'timeline'
    if (raw === 'map') return 'map'
    return 'cards'
  } catch {
    return 'cards'
  }
}

/** Persist the view mode. Best-effort — silent on storage failure. */
function saveViewMode(mode: ViewMode): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  } catch {
    /* ignore */
  }
}

/**
 * Strip HTML tags from a rendered-markdown string via a detached
 * element's `textContent`. The result is safe to truncate at an
 * arbitrary character offset without leaving a half-open tag.
 * Used for the card-preview teaser. Empty input returns ''. The
 * DOM operation is offline — the element is never attached, so
 * no layout / style work runs.
 *
 * Takes already-rendered HTML rather than markdown source so the
 * caller can render the markdown once and pass the result to
 * both the full-description rendering and this helper. `renderCards()`
 * re-runs on every keystroke during search; rendering the
 * markdown twice per card was wasted parse + sanitize work on
 * every keystroke.
 */
function htmlToPlainText(html: string | null | undefined): string {
  if (!html) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return (tmp.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * Phase 3pg/D — render a one-line "frame timeline" for an image-
 * sequence dataset's browse card. Shows the frame count + first /
 * last timestamps + a "closest to now" marker on a thin horizontal
 * track. Returns an empty string for non-sequence rows so the
 * caller can unconditionally interpolate the result.
 */
function renderFrameScrubber(d: Dataset): string {
  const frames = d.frames
  if (!frames || frames.count <= 0) return ''
  const countLabel = formatNumber(frames.count)
  if (!d.startTime || !d.period) {
    return `<div class="browse-card-scrubber browse-card-scrubber-pure">${escapeHtml(t('browse.card.scrubber.framesOnly', { count: countLabel }))}</div>`
  }
  const periodMs = parseIsoDurationMs(d.period)
  const startMs = Date.parse(d.startTime)
  if (periodMs == null || periodMs <= 0 || Number.isNaN(startMs)) {
    return `<div class="browse-card-scrubber browse-card-scrubber-pure">${escapeHtml(t('browse.card.scrubber.framesOnly', { count: countLabel }))}</div>`
  }
  // AYNI: a `P1M` or `P1Y` period steps calendar months, not the mean month (frameCalendar.ts).
  const axis = createFrameAxis({ startMs, frameCount: frames.count, cadenceMs: periodMs })
  const endMs = axisEndMs(axis)
  const nowMs = Date.now()
  const rawPos = (nowMs - startMs) / (endMs - startMs)
  const pos = Math.max(0, Math.min(1, rawPos))
  const posPct = (pos * 100).toFixed(1)
  const inWindow = nowMs >= startMs && nowMs < endMs
  const nowLabel = inWindow
    ? t('browse.card.scrubber.now')
    : nowMs < startMs
      ? t('browse.card.scrubber.beforeStart')
      : t('browse.card.scrubber.afterEnd')
  const startLabel = formatDate(d.startTime)
  const lastFrameIso = new Date(lastFrameTimeMs(axis)).toISOString()
  const endLabel = formatDate(lastFrameIso)
  return `
    <div class="browse-card-scrubber" role="img" aria-label="${escapeAttr(
      t('browse.card.scrubber.aria', { count: countLabel, start: startLabel, end: endLabel }),
    )}">
      <div class="browse-card-scrubber-label">
        <span class="browse-card-scrubber-count">${escapeHtml(t('browse.card.scrubber.frames', { count: countLabel }))}</span>
        <span class="browse-card-scrubber-now">${escapeHtml(nowLabel)}</span>
      </div>
      <svg class="browse-card-scrubber-track" viewBox="0 0 100 6" preserveAspectRatio="none" aria-hidden="true">
        <rect x="0" y="2" width="100" height="2" rx="1" class="browse-card-scrubber-bar"></rect>
        <circle cx="${posPct}" cy="3" r="1.6" class="browse-card-scrubber-dot${inWindow ? '' : ' browse-card-scrubber-dot-outside'}"></circle>
      </svg>
      <div class="browse-card-scrubber-ends">
        <span>${escapeHtml(formatDate(d.startTime))}</span>
        <span>${escapeHtml(formatDate(lastFrameIso))}</span>
      </div>
    </div>`
}

const SEARCH_FOCUS_DELAY_MS = 200
/** Debounce window between the last keystroke and the
 * `browse_search` Tier B emit. 400 ms feels right for a search-as-you
 * type box: long enough that `"hurricane"` is one event instead of
 * nine, short enough that the event lands while the user is still
 * looking at the results. */
const BROWSE_SEARCH_DEBOUNCE_MS = 400

/** Callbacks the browse UI uses to communicate with the main app. */
/** One satellite a dataset is built from, as Browse names it (owner, 2026-10-24; from the layer's sources.json). */
export interface BrowseSatelliteSource {
  norad: number
  name: string
  /** Drawable: in a mirrored GP group. */
  tracked: boolean
  /** For one not tracked: "retired 2018…" */
  note: string | null
}

let satelliteSourcesOf: ((datasetId: string) => readonly BrowseSatelliteSource[]) | null = null

/** The app lends Browse the satellites each dataset is built from (null: no line). */
/**
 * AYNI — Solar System mode (owner, 2026-11-04): Browse keeps to one body's
 * rows while that body is looked at (`values`: its body names, lower
 * case, the Earth as `earth`), or to every row again with null. Applied
 * at once when Browse is built, else when it is.
 */
let bodyScopeApplier: ((values: readonly string[] | null) => void) | null = null
let pendingBodyScope: readonly string[] | null | undefined
export function setBrowseBodyScope(values: readonly string[] | null): void {
  bodyScopeNow = values?.length ? [...values] : null
  if (bodyScopeApplier) bodyScopeApplier(values)
  else pendingBodyScope = values
}

/**
 * AYNI (owner on Quest, 2026-11-05: "the filtering by celestial body works
 * outside VR, but not inside"): the body list Browse keeps to now, and its
 * chip's name, so the headset's Browse (vrBrowse) keeps to the same rows
 * and shows the same chip; null when it shows every body.
 */
let bodyScopeNow: readonly string[] | null = null
export function browseBodyScope(): { values: readonly string[]; label: string } | null {
  return bodyScopeNow ? { values: bodyScopeNow, label: bodyScopeLabel(bodyScopeNow) } : null
}

/** The chip's name for a body scope: the body the list is about (its first value). */
let bodyScopeLabel: (values: readonly string[]) => string = (values) => values[0] ?? ''
export function setBrowseBodyScopeLabel(fn: (values: readonly string[]) => string): void {
  bodyScopeLabel = fn
}

export function setBrowseSatelliteSources(fn: ((datasetId: string) => readonly BrowseSatelliteSource[]) | null): void {
  satelliteSourcesOf = fn
}

/** The "Data from" line of a card: the satellites, each a button; one not tracked is named, greyed. */
function renderDataFrom(d: Dataset): string {
  const list = satelliteSourcesOf?.(d.id) ?? []
  if (!list.length) return ''
  return `<div class="browse-card-datafrom" title="${escapeAttr(t('browse.card.dataFrom.tip'))}"><span class="browse-card-datafrom-label">${escapeHtml(t('browse.card.dataFrom'))}</span> ${list
    .map((s) => `<button type="button" class="browse-card-satellite" data-norad="${s.norad}" data-tracked="${s.tracked}"${s.note ? ` title="${escapeAttr(s.note)}"` : ''}>${escapeHtml(s.name)}</button>`)
    .join(' ')}</div>`
}

export interface BrowseCallbacks {
  onSelectDataset: (id: string) => void
  /** Owner, 2026-10-24: a satellite named on a card ("Data from") is shown on the globe. */
  onShowSatellite?: (norad: number, name: string) => void
  announce: (message: string) => void
  isMobile: boolean
  onOpenChat?: (query?: string) => void
  /** AYNI: Start the lesson, from the Lessons tab. */
  onStartLesson?: (slug: string) => void
  /** AYNI: open one of the teacher's own saved lessons (My lessons). */
  onOpenKhipu?: (k: import('../services/khipu').Khipu, files?: Array<{ name: string; body: string }>) => void
  /** AYNI: the Khipu editor: a story or briefing from My Khipus, or a new one (`unsaved`: kept nowhere yet). */
  onEditKhipu?: (k: import('../services/khipu').Khipu, files?: Array<{ name: string; body: string }>, opts?: { unsaved?: boolean }) => void
  onNewKhipu?: (trigger: HTMLElement) => void
  /** AYNI: record a Khipu (Browse → Khipus); `recordState` says whether one can start. */
  onRecordKhipu?: (trigger: HTMLElement) => void
  recordState?: () => 'ready' | 'busy' | 'room'
  /** AYNI (multi-globe plan): open the dataset on a second globe beside the one shown (the dataset menu's "Open beside"). */
  onOpenBeside?: (id: string) => void
}

/**
 * Notify analytics that the browse overlay just transitioned from
 * hidden / collapsed to visible. Caller is responsible for deciding
 * whether the transition actually happened — this just emits the
 * event and (re)starts the dwell handle if it isn't already running.
 *
 * Exported so the main app's "re-open from collapsed" path
 * (`openBrowsePanel` in main.ts, which skips `showBrowseUI` to
 * avoid duplicating event listeners) can still book-keep telemetry
 * cleanly.
 */
export function notifyBrowseOpened(
  source: 'tools' | 'orbit' | 'shortcut' = 'tools',
): void {
  emit({ event_type: 'browse_opened', source })
  if (!browseDwellHandle) {
    browseDwellHandle = startDwell('browse')
  }
}

// ---------------------------------------------------------------------------
// Chip rail rendering helpers
// ---------------------------------------------------------------------------

/**
 * One option inside a multi-select chip group.
 *
 * `value` is the predicate value stored in {@link FilterState}; `label`
 * is the localised display string. Kept separate so the engine can
 * stay locale-independent (the URL-encoded form is `value`, never
 * `label`).
 */
interface ChipOption {
  value: string
  label: string
}

/**
 * Render a multi-select chip group. Each chip is a `<button>` with
 * `aria-pressed` reflecting whether the value is currently in the
 * facet's `multi-select` predicate. Clicks call back into
 * {@link onToggle} with the value; the caller decides how to mutate
 * filter state and re-render.
 *
 * `tagCloud` is set for the Category & content group only: its row
 * can collapse to a 2-row preview with a "Show more / Show fewer"
 * toggle (see {@link TagCloudRender}).
 */
/** AYNI: the words for data kinds, themes and packages (`datasetTaxonomy`). */
const KIND_LABEL: Record<DataKind, MessageKey> = {
  'real-time': 'browse.kind.realTime',
  forecast: 'browse.kind.forecast',
  projection: 'browse.kind.projection',
  reanalysis: 'browse.kind.reanalysis',
  historical: 'browse.kind.historical',
  catalogue: 'browse.kind.catalogue',
}
const PACKAGE_LABEL: Record<StreamPackage, MessageKey> = {
  'xr-atmosphere': 'xr.package.atmosphere.name',
  'xr-ocean': 'xr.package.ocean.name',
  'xr-climate': 'xr.package.climate.name',
  'xr-fire-aq': 'xr.package.fireAq.name',
  'xr-hazards': 'browse.package.hazards',
}

/** AYNI: what kind of data a card holds, and what it belongs to — the first thing a card says. */
function renderCardBadges(d: Dataset, packages: ReadonlySet<string> | 'all' | null = null): string {
  const kind = dataKindOf(d)
  const pkg = packageOf(d)
  const kindBadge = `<span class="browse-badge browse-badge-kind" data-kind="${kind}">${escapeHtml(t(KIND_LABEL[kind]))}</span>`
  // AYNI: a stream outside the licence's packages gets a lock and opens the
  // plans (owner, 2026-10-03: "upgrade hint"). It still plays: licensing
  // step 4's observe mode marks paid streams before anything is blocked.
  const locked = packages ? outsidePlan(d, packages, educationStreamsAllowed()) : null
  let second = ''
  if (locked && isEducationOnly(d)) {
    // AYNI (owner, 2026-10-20): a stream only Education plays says so, not a package the plan may already have.
    second = `<button type="button" class="browse-badge browse-badge-package browse-badge-locked" data-package="${locked}"`
      + ` title="${escapeAttr(t('browse.package.education.title'))}"`
      + ` aria-label="${escapeAttr(t('browse.package.education.aria'))}">`
      + `<span aria-hidden="true">&#x1F512;&#xFE0E;</span> ${escapeHtml(t('browse.package.education'))}</button>`
  } else if (locked) {
    const name = t(PACKAGE_LABEL[locked])
    second = `<button type="button" class="browse-badge browse-badge-package browse-badge-locked" data-package="${locked}"`
      + ` title="${escapeAttr(t('browse.package.locked.title', { package: name }))}"`
      + ` aria-label="${escapeAttr(t('browse.package.locked.aria', { package: name }))}">`
      + `<span aria-hidden="true">&#x1F512;&#xFE0E;</span> ${escapeHtml(name)}</button>`
  } else if (pkg) {
    second = `<span class="browse-badge browse-badge-package" data-package="${pkg}" title="${escapeAttr(t('browse.package.title'))}">${escapeHtml(t(PACKAGE_LABEL[pkg]))}</span>`
  } else if (kind === 'catalogue') {
    second = `<span class="browse-badge browse-badge-free">${escapeHtml(t('browse.badge.free'))}</span>`
  }
  return `<div class="browse-card-badges">${kindBadge}${second}${renderCompatibility(d)}</div>`
}

/**
 * One dataset card, as the Catalog and Recent tabs list it: thumbnail,
 * title, kind and package, then (opened) the description and details.
 * AYNI (2026-10-03): compact — the description shows only when a card
 * is opened, so the panel lists many rows, not two.
 */
function renderCardHtml(d: Dataset, packages: ReadonlySet<string> | 'all' | null): string {
  // AYNI: a stream's enriched categories are keyed "Source" and "Topic",
  // which say nothing; its badges carry the kind and package instead.
  const cats = d.enriched?.categories && !d.realtimeKind ? Object.keys(d.enriched.categories).slice(0, MAX_CARD_CATEGORIES) : []
  const rawDesc = d.enriched?.description ?? d.abstractTxt ?? ''
  const fullDescRendered = renderMarkdown(rawDesc)
  const plainDesc = htmlToPlainText(fullDescRendered)
  const shortDesc = plainDesc.length > CARD_DESCRIPTION_MAX_LENGTH
    ? plainDesc.substring(0, CARD_DESCRIPTION_MAX_LENGTH).trim() + '…'
    : plainDesc

  const catsHtml = cats.length
    ? `<div class="browse-card-cats">${cats.map(c => `<span class="browse-card-cat">${escapeHtml(c)}</span>`).join('')}</div>`
    : ''
  const shortDescHtml = shortDesc
    ? `<p class="browse-card-desc">${escapeHtml(shortDesc)}</p>`
    : ''

  const fullDescHtml = fullDescRendered
    ? `<div class="browse-card-full-desc">${fullDescRendered}</div>`
    : ''
  const org = d.organization
  const dev = d.enriched?.datasetDeveloper?.name
  const visDev = d.enriched?.visDeveloper?.name
  const dateAdded = d.enriched?.dateAdded
  const keywords = [...(d.enriched?.keywords ?? []), ...(d.tags ?? [])]
  const catalogUrl = d.enriched?.catalogUrl
  const timeRange = !(d.startTime && d.endTime)
    ? ''
    : hasModelYearCoverage(d)
      ? escapeHtml(t('browse.card.meta.modelYears', {
          start: extractYear(d.startTime) ?? '',
          end: extractYear(d.endTime) ?? '',
        }))
      : `${formatDate(d.startTime)} – ${formatDate(d.endTime)}`

  let metaHtml = ''
  if (org) metaHtml += `<div class="browse-card-meta"><strong>${escapeHtml(t('browse.card.meta.source'))}</strong> ${escapeHtml(org)}</div>`
  if (dev) metaHtml += `<div class="browse-card-meta"><strong>${escapeHtml(t('browse.card.meta.datasetDeveloper'))}</strong> ${escapeHtml(dev)}</div>`
  if (visDev) metaHtml += `<div class="browse-card-meta"><strong>${escapeHtml(t('browse.card.meta.visualization'))}</strong> ${escapeHtml(visDev)}</div>`
  if (timeRange) metaHtml += `<div class="browse-card-meta"><strong>${escapeHtml(t('browse.card.meta.timeRange'))}</strong> ${timeRange}</div>`
  const scrubberHtml = renderFrameScrubber(d)
  if (scrubberHtml) metaHtml += scrubberHtml
  if (dateAdded) metaHtml += `<div class="browse-card-meta"><strong>${escapeHtml(t('browse.card.meta.added'))}</strong> ${escapeHtml(addedDay(dateAdded))}</div>`
  // AYNI (2026-10-24): a page off NOAA's catalogue (a NASA picture AYNI made) is "the source page", not "the SOS catalog".
  if (catalogUrl) metaHtml += `<div class="browse-card-meta"><a href="${escapeAttr(catalogUrl)}" target="_blank" rel="noopener" style="color: var(--color-accent); text-decoration: none; font-size: 0.65rem;">${escapeHtml(t(isSosCatalogPage(catalogUrl) ? 'browse.card.catalogLink' : 'browse.card.sourceLink'))}</a></div>`

  const keywordsHtml = keywords.length
    ? `<div class="browse-card-keywords">${keywords.slice(0, MAX_CARD_KEYWORDS).map(k => `<span class="browse-card-keyword" data-keyword="${escapeAttr(k)}" role="button" tabindex="0" aria-label="${escapeAttr(t('browse.card.keyword.aria', { keyword: k }))}">${escapeHtml(k)}</span>`).join('')}</div>`
    : ''

  const thumbHtml = d.thumbnailLink
    ? `<img class="browse-card-thumb" src="${escapeAttr(d.thumbnailLink)}" alt="${escapeAttr(t('browse.card.thumb.alt', { title: d.title }))}" loading="lazy">`
    : ''

  // The zip-download affordance (📦). `isZipDownloadable` suppresses
  // the button on datasets we know will fail today (plain HLS-only
  // videos post Phase 3 r2-hls migration) — see the comment on the
  // helper; widen the check once #147 / #148 land.
  const zipBtn = isZipDownloadable(d)
    ? `<button class="browse-card-zip" data-id="${escapeAttr(d.id)}"`
      + ` aria-label="${escapeAttr(t('zip.action.zipDownload.aria', { title: d.title }))}"`
      + ` title="${escapeAttr(t('zip.action.zipDownload.title'))}">&#x1F4E6;</button>`
    : ''

  // AYNI: "+" opens the dataset menu (`datasetMenu.ts`) — the same menu as
  // a right-click or a long press, and the way in on a headset.
  const menuBtn = `<button class="browse-card-menu"`
    + ` data-dataset-id="${escapeAttr(d.id)}" aria-haspopup="menu" aria-expanded="false"`
    + ` aria-label="${escapeAttr(t('datasetMenu.button.aria', { title: d.title }))}"`
    + ` title="${escapeAttr(t('datasetMenu.button.title'))}"`
    + `>+</button>`

  return `<div class="browse-card" data-id="${escapeAttr(d.id)}" role="listitem" tabindex="0" aria-label="${escapeAttr(d.title)}" aria-expanded="false">
      ${thumbHtml}
      <div class="browse-card-body">
        <div class="browse-card-header">
          <span class="browse-card-title">${escapeHtml(displayTitle(d))}</span>
          ${menuBtn}
          ${zipBtn}
          <button class="browse-card-load" data-id="${escapeAttr(d.id)}" aria-label="${escapeAttr(t('browse.card.load.aria', { title: d.title }))}">${escapeHtml(t('browse.card.load'))}</button>
        </div>
        ${renderCardBadges(d, packages)}${renderDataFrom(d)}${catsHtml}${shortDescHtml}
        <div class="browse-card-details">
          ${fullDescHtml}${metaHtml}${keywordsHtml}
        </div>
      </div>
    </div>`
}

function renderChipGroup(
  facet: string,
  groupLabel: string,
  options: readonly ChipOption[],
  active: ReadonlySet<string>,
  ariaLabel: string,
  tagCloud?: TagCloudRender,
): string {
  if (options.length === 0) return ''
  const chips = options
    .map(o => {
      const isActive = active.has(o.value)
      return `<button type="button" class="browse-chip${isActive ? ' active' : ''}" data-facet="${escapeAttr(facet)}" data-value="${escapeAttr(o.value)}" aria-pressed="${isActive}">${escapeHtml(o.label)}</button>`
    })
    .join('')
  const clamped = !!tagCloud && tagCloud.worthClamping && !tagCloud.expanded
  const rowAttrs = tagCloud ? ' data-tag-cloud' : ''
  const toggle = tagCloud
    ? `<button type="button" class="browse-chip-rail-toggle${tagCloud.worthClamping ? '' : ' hidden'}" data-tag-cloud-toggle aria-expanded="${tagCloud.expanded}">${escapeHtml(tagCloudToggleLabel(tagCloud.expanded))}</button>`
    : ''
  return `
    <div class="browse-filter-group">
      <div class="browse-filter-label">${escapeHtml(groupLabel)}</div>
      <div class="browse-chip-row${clamped ? ' is-clamped' : ''}"${rowAttrs} role="group" aria-label="${escapeAttr(ariaLabel)}">${chips}</div>
      ${toggle}
    </div>`
}

/**
 * Render-time state of the Category & content tag cloud.
 *
 * Whether the cloud needs clamping depends on layout (how many rows
 * the chips wrap to at the panel's current width), not on the chip
 * count, so `showBrowseUI` measures the rendered row after every
 * render and on resize and patches the clamp class and toggle in
 * place. `worthClamping` is the last measured answer, used so a
 * re-render paints the right state before it can be measured again.
 */
interface TagCloudRender {
  /** Expanded past the 2-row preview (by the user, or at boot for
   *  an active category). */
  expanded: boolean
  /** The 2-row preview hides enough of the chips to save more room
   *  than its toggle takes up. */
  worthClamping: boolean
}

/** Label for the tag cloud's toggle in the given state. */
function tagCloudToggleLabel(expanded: boolean): string {
  return expanded ? t('browse.filter.tags.less') : t('browse.filter.tags.more')
}

/**
 * Render a boolean facet as a single toggle chip. Same shape as
 * {@link renderChipGroup} but with one option and the click semantics
 * map onto the engine's `toggleFacet(state, facet, '')` (the empty-
 * string value creates a `kind: 'boolean'` predicate per the engine
 * contract).
 */
function renderBooleanChip(
  facet: string,
  label: string,
  isActive: boolean,
  extraTitle?: string,
): string {
  const titleAttr = extraTitle ? ` title="${escapeAttr(extraTitle)}"` : ''
  return `<button type="button" class="browse-toggle-chip${isActive ? ' active' : ''}" data-facet="${escapeAttr(facet)}" data-toggle="boolean" aria-pressed="${isActive}"${titleAttr}>${escapeHtml(label)}</button>`
}

/**
 * Render a year-range filter as a pair of number inputs. Low-fi UI
 * intentional — the dual-thumb slider that §6.1 sketches is a
 * follow-up. Two numeric inputs are accessible, mobile-friendly,
 * and exercise the engine's range predicate without a new component.
 */
function renderRangeInputs(
  facet: string,
  label: string,
  fromLabel: string,
  toLabel: string,
  predicate: FacetPredicate | undefined,
  bounds: { min: number; max: number },
): string {
  const current = predicate?.kind === 'range' ? predicate : undefined
  const minVal = current?.min != null ? String(current.min) : ''
  const maxVal = current?.max != null ? String(current.max) : ''
  return `
    <div class="browse-filter-group">
      <div class="browse-filter-label">${escapeHtml(label)}</div>
      <div class="browse-range-row" role="group" aria-label="${escapeAttr(label)}">
        <label class="sr-only" for="browse-range-${escapeAttr(facet)}-min">${escapeHtml(fromLabel)}</label>
        <input
          type="number"
          id="browse-range-${escapeAttr(facet)}-min"
          class="browse-range-input"
          data-facet="${escapeAttr(facet)}"
          data-bound="min"
          inputmode="numeric"
          min="${bounds.min}"
          max="${bounds.max}"
          placeholder="${escapeAttr(String(bounds.min))}"
          value="${escapeAttr(minVal)}"
          aria-label="${escapeAttr(fromLabel)}" />
        <span class="browse-range-sep" aria-hidden="true">–</span>
        <label class="sr-only" for="browse-range-${escapeAttr(facet)}-max">${escapeHtml(toLabel)}</label>
        <input
          type="number"
          id="browse-range-${escapeAttr(facet)}-max"
          class="browse-range-input"
          data-facet="${escapeAttr(facet)}"
          data-bound="max"
          inputmode="numeric"
          min="${bounds.min}"
          max="${bounds.max}"
          placeholder="${escapeAttr(String(bounds.max))}"
          value="${escapeAttr(maxVal)}"
          aria-label="${escapeAttr(toLabel)}" />
      </div>
    </div>`
}

/**
 * Collect the unique tag values across the visible catalog, sorted
 * by frequency descending. Mirrors §6.1's audited ordering — Water
 * (80), People (63), Air (56), Land (39), … — but derives from the
 * actual dataset set so a future catalog change is reflected
 * automatically. Tags that don't appear on any visible row don't
 * surface as chips.
 */
function collectTagOptions(datasets: readonly Dataset[]): ChipOption[] {
  const counts = new Map<string, number>()
  for (const d of datasets) {
    if (d.isHidden) continue
    for (const tag of d.tags ?? []) {
      counts.set(tag, (counts.get(tag) ?? 0) + 1)
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([value]) => ({ value, label: value }))
}

/**
 * The four typed-group section keys. Listed in §6.1's display
 * order; the accordion state map is keyed off these strings.
 */
type SectionKey = 'kind' | 'category' | 'format' | 'time' | 'quality'
const SECTION_KEYS: readonly SectionKey[] = ['kind', 'category', 'format', 'time', 'quality']

/** Section open/collapsed state. Persisted to localStorage so the
 *  user's expand/collapse choices stick across sessions. */
type SectionOpenState = Record<SectionKey, boolean>

/** localStorage key holding the SectionOpenState JSON blob.
 *  Versioned suffix so a future schema change can ignore stale
 *  shapes without crashing the rail. */
const SECTION_OPEN_STORAGE_KEY = 'sos-browse-section-open.v1'

/**
 * Default open/collapsed state for each typed group on first
 * visit. Category & content opens because it's the highest-
 * frequency surface; the rest collapse to save vertical space
 * per the user's accordion request. Active filters auto-expand
 * their section so a shared URL or prefix-search never leaves
 * filtered state hidden — that logic lives in
 * {@link computeSectionOpenState}.
 */
const SECTION_OPEN_DEFAULT: SectionOpenState = {
  // AYNI: what kind of data, and about what, comes first and open.
  kind: true,
  category: false,
  format: false,
  time: false,
  quality: false,
}

/**
 * Load the persisted section open/collapsed map. Returns the
 * default when localStorage is unavailable or the stored value
 * is unparseable / from a previous schema. SSR-safe — guards on
 * `typeof window`.
 */
function loadSectionOpenState(): SectionOpenState {
  if (typeof window === 'undefined') return { ...SECTION_OPEN_DEFAULT }
  try {
    const raw = window.localStorage.getItem(SECTION_OPEN_STORAGE_KEY)
    if (!raw) return { ...SECTION_OPEN_DEFAULT }
    const parsed = JSON.parse(raw) as Partial<SectionOpenState>
    const result: SectionOpenState = { ...SECTION_OPEN_DEFAULT }
    for (const key of SECTION_KEYS) {
      if (typeof parsed[key] === 'boolean') result[key] = parsed[key] as boolean
    }
    return result
  } catch {
    return { ...SECTION_OPEN_DEFAULT }
  }
}

/** Persist the section open/collapsed state. Best-effort — swallow
 *  storage failures (private-mode browsers, quota exceeded) so the
 *  rail keeps working. */
function saveSectionOpenState(state: SectionOpenState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SECTION_OPEN_STORAGE_KEY, JSON.stringify(state))
  } catch {
    /* ignore */
  }
}

/**
 * Count active facet predicates within a section. Used both to
 * decide whether to auto-expand a collapsed section and to render
 * the badge in the section header (so a collapsed section still
 * signals "you have something filtered here").
 *
 *  - multi-select  → `values.length`
 *  - boolean       → 1 when present
 *  - range         → 1 when min or max is set
 *  - bbox          → 1 when present
 */
function countSectionActive(section: SectionKey, state: FilterState): number {
  const facets = SECTION_FACETS[section]
  let count = 0
  for (const facet of facets) {
    const predicate = state[facet]
    if (predicate == null) continue
    if (predicate.kind === 'multi-select') count += predicate.values.length
    else if (predicate.kind === 'boolean') count += 1
    else if (predicate.kind === 'range') {
      if (predicate.min != null || predicate.max != null) count += 1
    } else if (predicate.kind === 'bbox') count += 1
  }
  return count
}

/** Map from typed-group section to the facets it owns. Single
 *  source of truth for the section ↔ facet association. */
const SECTION_FACETS: Readonly<Record<SectionKey, readonly string[]>> = {
  kind: ['dataKind', 'theme', 'satellite'],
  category: ['category'],
  format: ['format'],
  time: ['recentlyViewed', 'dateAdded', 'dataCoverageYear'],
  quality: ['hasCaptions', 'hasStory'],
}

/**
 * Merge the persisted open state with the auto-expand rule:
 * sections whose facets are active are forced open, since hiding
 * filtered chips behind a collapsed header would surprise the
 * user (especially on a shared link). The user can still
 * collapse the section by clicking the header — the auto-expand
 * only applies when reading from persistence, not after the user
 * has explicitly toggled.
 */
function computeSectionOpenState(
  persisted: SectionOpenState,
  filterState: FilterState,
): SectionOpenState {
  const result = { ...persisted }
  for (const section of SECTION_KEYS) {
    if (countSectionActive(section, filterState) > 0) result[section] = true
  }
  return result
}

/**
 * Render an accordion-style section header. Clickable button with
 * a `▸`/`▾` indicator and an `aria-expanded` state. Includes an
 * active-filter badge when the section has chips set so a
 * collapsed section still signals filtered state. The arrow
 * indicator is hardcoded — it's a visual glyph, not a label, and
 * doesn't need to flip in RTL (RTL accordion conventions still
 * use `▸`/`▾` going down rather than `◂`/`▾`).
 */
function renderSectionHeader(
  section: SectionKey,
  title: string,
  isOpen: boolean,
  activeCount: number,
): string {
  const badge = activeCount > 0
    ? ` <span class="browse-filter-section-badge">${escapeHtml(formatNumber(activeCount))}</span>`
    : ''
  const indicator = isOpen ? '▾' : '▸'
  return `<button type="button" class="browse-filter-section-header" data-section="${escapeAttr(section)}" aria-expanded="${isOpen}"><span class="browse-filter-section-indicator" aria-hidden="true">${indicator}</span><span class="browse-filter-section-title">${escapeHtml(title)}</span>${badge}</button>`
}

/**
 * Year bounds for the date-added and data-coverage range inputs.
 * Lowest year + highest year across the visible catalog. Used to
 * seed the input `min`/`max` attributes so the range inputs don't
 * accept gibberish.
 */
function computeYearBounds(
  datasets: readonly Dataset[],
  extract: (d: Dataset) => number | undefined,
  fallback: { min: number; max: number },
): { min: number; max: number } {
  let min = Infinity
  let max = -Infinity
  for (const d of datasets) {
    if (d.isHidden) continue
    const year = extract(d)
    if (year == null) continue
    if (year < min) min = year
    if (year > max) max = year
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return fallback
  return { min, max }
}

/**
 * When a row was first published, in ms, for the Newest sort; null when
 * it has no date. Parsed rather than compared as text: the library's
 * dates are days (`2021-02-01`), a stream's may carry a time.
 */
function publishedTime(d: Dataset): number | null {
  const ms = Date.parse(d.enriched?.dateAdded ?? '')
  return Number.isNaN(ms) ? null : ms
}

function dateAddedYear(d: Dataset): number | undefined {
  const v = d.enriched?.dateAdded
  if (!v) return undefined
  const m = v.match(/^(\d{4})/)
  return m ? Number(m[1]) : undefined
}

function coverageStartYear(d: Dataset): number | undefined {
  if (!d.startTime) return undefined
  const m = d.startTime.match(/^(\d{4})/)
  return m ? Number(m[1]) : undefined
}

function coverageEndYear(d: Dataset): number | undefined {
  const source = d.endTime ?? d.startTime
  if (!source) return undefined
  const m = source.match(/^(\d{4})/)
  return m ? Number(m[1]) : undefined
}

// ---------------------------------------------------------------------------
// Main entry — show the browse overlay
// ---------------------------------------------------------------------------

/**
 * Render and display the dataset browse overlay with the new
 * typed-group chip rail, search, sort controls, and dataset cards.
 *
 * Wires every interactive element idempotently — re-calling
 * `showBrowseUI` after the overlay was hidden is safe and does not
 * duplicate listeners. Filter state lives in a closure variable
 * per-overlay-instance; future URL-persistence and search-prefix
 * wiring layer on top of this base.
 */
export function showBrowseUI(
  datasets: Dataset[],
  callbacks: BrowseCallbacks,
  source: 'tools' | 'orbit' | 'shortcut' = 'tools',
): void {
  const overlay = document.getElementById('browse-overlay')
  if (!overlay) return
  const wasHidden = overlay.classList.contains('hidden')
  const wasCollapsed = overlay.classList.contains('collapsed')
  overlay.classList.remove('hidden')
  overlay.classList.remove('collapsed')
  document.body.classList.add('browse-open')
  if (wasHidden || wasCollapsed) {
    notifyBrowseOpened(source)
  }

  // Re-call short-circuit. The chip rail, search input, sort
  // buttons and view-mode toggle wire their listeners once via
  // `dataset.wired` checks — those listeners CAPTURE this
  // function's closure-local `filterState`, `viewMode`,
  // `graphController`, and `applyState`. A second showBrowseUI
  // call (e.g. via main.ts's catalog↔sphere tab handler, which
  // re-runs showBrowseUI after a Sphere round-trip) creates a
  // fresh closure but the listeners stay bound to the original
  // one, so chip clicks would call the OLD applyState — reading
  // a stale `viewMode='cards'` and `graphController=null` even
  // though the user has since toggled into Graph view. That
  // exact bug surfaced in PR #137 review ("clicking a chip
  // doesn't update the graph").
  //
  // The fix: subsequent calls only un-hide the overlay (above)
  // and emit `browse_opened`; rendering and listener wiring are
  // skipped. The original openBrowsePanel call site in main.ts
  // already gated on `browseInitialized`, but other call sites
  // (catalog-empty boot, catalog↔sphere tab) didn't. Centralising
  // the gate here makes the invariant uniform regardless of caller.

  if (overlay.dataset.browseInitialized === 'true') return

  // Wire the in-header help trigger once (idempotent)
  const helpBtn = document.getElementById('help-trigger-browse')
  if (helpBtn && !helpBtn.dataset.wired) {
    helpBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      toggleHelp(helpBtn)
    })
    helpBtn.dataset.wired = 'true'
  }

  // AYNI: the in-header chat button opens the site's assistant (the AYNI
  // chatbot), with the search so far as the question. terraviz's chat UI
  // used to wire it itself; it went with that assistant (2026-10-03).
  const chatBtn = document.getElementById('browse-chat-btn')
  if (chatBtn && !chatBtn.dataset.wired) {
    chatBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      callbacks.onOpenChat?.(searchQuery || undefined)
    })
    chatBtn.dataset.wired = 'true'
  }

  // Wire the in-header close button once (idempotent). Always fully
  // hides the overlay — the Tools menu's Browse button re-opens it.
  const closeBtn = document.getElementById('browse-close')
  if (closeBtn && !closeBtn.dataset.wired) {
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      hideBrowseUI()
      callbacks.announce(t('browse.announce.closed'))
    })
    closeBtn.dataset.wired = 'true'
  }

  // The full catalog the rail operates on. The engine handles
  // hidden-dataset exclusion, so we pass the unfiltered list and
  // trust `filterDatasets`.
  const allDatasets = [...datasets]
    .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0) || a.title.localeCompare(b.title))

  // Pre-compute chip options + year bounds from the full visible
  // set (excluding hidden rows). The chip rail surfaces tags that
  // are actually on a visible row.
  const baselineVisible = allDatasets.filter(d => !d.isHidden)
  const tagOptions = collectTagOptions(baselineVisible)
  // AYNI: only the kinds and themes some visible row has.
  const kindsPresent = new Set(baselineVisible.map(dataKindOf))
  const kindOptions: ChipOption[] = DATA_KINDS.filter((k) => kindsPresent.has(k)).map((k) => ({ value: k, label: t(KIND_LABEL[k]) }))
  const themesPresent = new Set(baselineVisible.flatMap(themesOf))
  const themeOptions: ChipOption[] = THEMES.filter((th) => themesPresent.has(th)).map((th) => ({ value: th, label: t(THEME_LABEL[th]) }))
  // AYNI (satellites ↔ catalog plan): the satellites the visible rows are built from, most datasets first.
  const satelliteCounts = new Map<string, number>()
  for (const d of baselineVisible) for (const s of satelliteSourcesOf?.(d.id) ?? []) satelliteCounts.set(s.name, (satelliteCounts.get(s.name) ?? 0) + 1)
  const satelliteOptions: ChipOption[] = [...satelliteCounts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name]) => ({ value: name, label: name }))
  const dateAddedBounds = computeYearBounds(baselineVisible, dateAddedYear, { min: 2000, max: new Date().getUTCFullYear() })
  const coverageBoundsPair = computeYearBounds(baselineVisible, coverageStartYear, { min: 1900, max: new Date().getUTCFullYear() + 100 })
  const coverageBoundsMax = computeYearBounds(baselineVisible, coverageEndYear, coverageBoundsPair)
  const coverageBounds = {
    min: Math.min(coverageBoundsPair.min, coverageBoundsMax.min),
    max: Math.max(coverageBoundsPair.max, coverageBoundsMax.max),
  }

  // Update search placeholder with actual count (baseline visible).
  const searchEl = document.getElementById('browse-search') as HTMLInputElement | null
  if (searchEl) {
    searchEl.placeholder = t('browse.search.placeholderCount', { count: baselineVisible.length })
  }

  type SortKey = 'relevance' | 'newest' | 'az'
  let activeSort: SortKey = 'relevance'
  // Live narrow-viewport check. Graph + Timeline views need
  // horizontal room (force-directed layout / time bars span the
  // available width), AND vertical room for the chip rail sitting
  // above them — both of which a portrait phone runs out of.
  //
  // The gate is portrait-only: below 768px wide AND in portrait,
  // hide the toggle and force Cards. A phone in landscape (e.g.
  // 667 × 375 SE, 852 × 393 iPhone 15) clears the gate so a user
  // testing from a phone can still reach the non-Cards surfaces.
  // Vertical space is tight in phone landscape — Timeline shows
  // only ~10–15 rows at the comfortable height and the Graph's
  // canvas is cramped — but the views are usable for smoke
  // testing, which is the explicit goal of allowing them here.
  //
  // matchMedia gives a live signal that re-fires on both width
  // changes (resize) AND orientation changes (rotate). We union
  // with `callbacks.isMobile` (the boot-time mobile flag), gated on
  // portrait via a second matchMedia so a mobile user in
  // landscape doesn't get force-funnelled back to Cards either.
  const narrowVpQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(max-width: 768px) and (orientation: portrait)')
    : null
  const portraitOnlyQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(orientation: portrait)')
    : null
  const isNarrowViewport = (): boolean => {
    if (narrowVpQuery?.matches) return true
    // A mobile device — `callbacks.isMobile` alone would gate
    // away even in landscape. Require portrait too so the
    // landscape parity holds across both delivery surfaces.
    if (callbacks.isMobile) {
      return portraitOnlyQuery?.matches ?? true
    }
    return false
  }
  // View-mode (§6.7+). Initial value falls back to Cards when the
  // viewport is currently narrow so a `viewMode='graph'` or
  // `'timeline'` left in localStorage from a desktop session doesn't
  // boot into an unusable canvas on a phone. Map view stays
  // available at every breakpoint per §6.9 — so a phone user who
  // last left Map view stays in Map on next launch.
  function bootViewMode(): ViewMode {
    const persisted = loadViewMode()
    if (isNarrowViewport() && (persisted === 'graph' || persisted === 'timeline')) {
      return 'cards'
    }
    return persisted
  }
  let viewMode: ViewMode = bootViewMode()
  // Lazy-mounted graph controller — populated on first toggle into
  // Graph view. Module-loaded once per overlay instance; the
  // controller itself handles re-renders on filter changes.
  let graphController: CatalogGraphController | null = null
  let graphLoadPromise: Promise<CatalogGraphController | null> | null = null
  // Same shape for the Timeline (§6.8). Lazy-loaded on first toggle
  // so the d3 chunk only ships when the user actually opens
  // Timeline view; mirrors the graph controller's lifecycle.
  let timelineController: CatalogTimelineController | null = null
  let timelineLoadPromise: Promise<CatalogTimelineController | null> | null = null
  // Same shape for the Map view (§6.9). Lazy-loaded on first toggle
  // so the second MapRenderer instance only mounts when the user
  // opens Map view; mirrors the graph + timeline controllers'
  // lifecycle. MapLibre itself is already in the bundle (the main
  // globe uses it), so the lazy mount only avoids the second
  // instance's tile-fetch warm-up cost.
  let mapController: CatalogMapController | null = null
  let mapLoadPromise: Promise<CatalogMapController | null> | null = null
  // The raw search-box string — both free text and any
  // `category:foo` / `format:bar` / `period:yearly` prefixes the
  // user has typed. `parseSearchQuery` splits it into the free-
  // text rest and a {@link FilterState} overlay merged onto chip
  // state at filter time (§6.2).
  let searchQuery = ''
  // Chip / range / toggle state — separate from search-prefix
  // overlay so a search like `category:Water hurricane` doesn't
  // visually light up the Water chip (the prefix is a parallel
  // input, not a chip mutation). Both feed `filterDatasets` via
  // mergeFilterStates so the predicate semantics stay identical.
  let filterState: FilterState = {}

  // Boot from URL — restore filter chips + search query from
  // ?cat=…&q=… so a shared catalog link reproduces the filter
  // surface. URL params we don't recognise are ignored
  // (forward-compat). Search query case is preserved through
  // the round-trip so `category:Water` from a shared link
  // still matches the canonical `Water` tag.
  const initialUrlState = readFilterStateFromUrl()
  if (Object.keys(initialUrlState.state).length > 0) {
    filterState = initialUrlState.state
  }
  if (initialUrlState.searchQuery) {
    searchQuery = initialUrlState.searchQuery
  }

  // §9.2 — mutable set of visited dataset ids backing the
  // Recently-viewed facet. Held by reference so the resolver below
  // closes over a stable object; `renderCards` refreshes its
  // contents in place (clear + re-add) before each filter pass so a
  // dataset opened mid-browse-session reflects immediately without
  // rebuilding the resolver map.
  const visitedIds = new Set<string>(getVisitedIds())
  const refreshVisited = (): void => {
    visitedIds.clear()
    for (const id of getVisitedIds()) visitedIds.add(id)
  }

  // Resolvers passed to the engine — baseline §6.1 set plus the
  // search-only `period:` resolver so `period:yearly` actually
  // filters, plus the §9.2 Recently-viewed resolver. Hoisted to
  // module-local once for the lifetime of this overlay instance;
  // rebuilding per render is wasted work.
  const resolvers = {
    ...BASELINE_RESOLVERS,
    period: PERIOD_RESOLVER,
    recentlyViewed: makeRecentlyViewedResolver(visitedIds),
  }

  // Fast id → Dataset lookup for the Continue-exploring row, which
  // resolves the most-recently-visited ids back to catalog rows.
  const datasetById = new Map<string, Dataset>(allDatasets.map(d => [d.id, d]))

  // Accordion section open/collapsed state — persisted to
  // localStorage so the user's expand/collapse choices stick
  // across sessions. The user explicitly asked for an accordion
  // to save screen real estate; defaults open Category & content
  // and collapse the other three groups. Sections with active
  // facets auto-expand at boot so a shared URL or prefix-search
  // never leaves filtered state hidden. Auto-expand considers
  // both URL-decoded facets AND any prefix tokens in the search
  // query (e.g. `?q=category:Water` opens Category even though
  // the chip state is empty), so the rule matches the comment.
  const bootEffectiveState = mergeFilterStates(
    filterState,
    parseSearchQuery(searchQuery).prefixes,
  )
  let sectionOpen: SectionOpenState = computeSectionOpenState(
    loadSectionOpenState(),
    bootEffectiveState,
  )
  // Whether the Category & content tag cloud is expanded past its
  // 2-row preview. Survives re-renders and lasts for the page:
  // reopening the panel returns early above (`browseInitialized`),
  // so a cloud the user expanded is still expanded when they come
  // back. Starts expanded when a category is active at boot, by the
  // same rule that auto-opens the section, so a shared `?cat=` link
  // never leaves its selected chip in the clipped rows. The user's
  // own toggles win after that.
  let chipRailExpanded = countSectionActive('category', bootEffectiveState) > 0
  // Last measured "clamping to 2 rows saves room". Assumed true
  // until the row is first laid out (a long cloud is the case the
  // clamp exists for), then kept from the latest measurement so a
  // re-render while the panel is hidden paints the last known state.
  let tagCloudWorthClamping = true

  // ----- Filter rail render -----

  const rail = document.getElementById('browse-filter-rail')

  /**
   * Render the entire filter rail from the current `filterState`.
   * Idempotent and pure-from-state — every interactive element's
   * `aria-pressed` / input value is derived from `filterState`, so
   * a re-render after any mutation is correct without diff
   * machinery.
   */
  function renderRail(): void {
    if (!rail) return
    const categoryActive = predicateValues(filterState.category)
    const formatActive = predicateValues(filterState.format)
    const formatOptions: ChipOption[] = FORMAT_BUCKETS.map(b => ({
      value: b,
      label: t(`browse.filter.format.${b}` as const),
    }))
    const hasCaptions = filterState.hasCaptions?.kind === 'boolean'
    const hasStory = filterState.hasStory?.kind === 'boolean'
    const recentlyViewed = filterState.recentlyViewed?.kind === 'boolean'
    // §9.2 — the Recently-viewed chip only makes sense once the user
    // has a visit history. With an empty log the predicate would
    // match nothing, so hide the chip entirely rather than offer a
    // toggle that empties the grid. The toggle stays visible while
    // active even if the log somehow drains, so the user can always
    // clear it.
    const hasVisitHistory = visitedIds.size > 0

    const sections: string[] = []

    /** Wrap a section's body in the accordion shell — clickable
     *  header + collapsible body. The body markup is always
     *  emitted; CSS `.collapsed .browse-filter-section-body
     *  { display: none }` hides it when the section is closed.
     *  The accordion-toggle handler flips the `collapsed` class
     *  in place rather than calling `renderRail()` so DOM input
     *  state (e.g. a partially-typed range-input value) survives
     *  a collapse-and-reopen cycle. */
    const wrapSection = (
      key: SectionKey,
      title: string,
      bodyHtml: string,
    ): string => {
      const isOpen = sectionOpen[key]
      const activeCount = countSectionActive(key, filterState)
      const header = renderSectionHeader(key, title, isOpen, activeCount)
      return `<div class="browse-filter-section${isOpen ? '' : ' collapsed'}" data-group="${escapeAttr(key)}">${header}<div class="browse-filter-section-body">${bodyHtml}</div></div>`
    }

    // AYNI: kind and theme first.
    sections.push(wrapSection(
      'kind',
      t('browse.filter.group.kind'),
      renderChipGroup('dataKind', t('browse.filter.kind.label'), kindOptions, predicateValues(filterState.dataKind), t('browse.filter.kind.aria'))
      + renderChipGroup('theme', t('browse.filter.theme.label'), themeOptions, predicateValues(filterState.theme), t('browse.filter.theme.aria'))
      + renderChipGroup('satellite', t('browse.filter.satellite.label'), satelliteOptions, predicateValues(filterState.satellite), t('browse.filter.satellite.aria')),
    ))

    // Category & content
    sections.push(wrapSection(
      'category',
      t('browse.filter.group.category'),
      renderChipGroup(
        'category',
        t('browse.filter.tags.label'),
        tagOptions,
        categoryActive,
        t('browse.filter.tags.aria'),
        { expanded: chipRailExpanded, worthClamping: tagCloudWorthClamping },
      ),
    ))

    // Format & medium
    sections.push(wrapSection(
      'format',
      t('browse.filter.group.format'),
      renderChipGroup(
        'format',
        t('browse.filter.format.label'),
        formatOptions,
        formatActive,
        t('browse.filter.format.aria'),
      ),
    ))

    // Time
    const recentlyViewedChip = (hasVisitHistory || recentlyViewed)
      ? `<div class="browse-toggle-row">${renderBooleanChip(
          'recentlyViewed',
          t('browse.filter.recentlyViewed.label'),
          recentlyViewed,
          t('browse.filter.recentlyViewed.help'),
        )}</div>`
      : ''
    sections.push(wrapSection(
      'time',
      t('browse.filter.group.time'),
      recentlyViewedChip + renderRangeInputs(
        'dateAdded',
        t('browse.filter.dateAdded.label'),
        t('browse.filter.dateAdded.fromLabel'),
        t('browse.filter.dateAdded.toLabel'),
        filterState.dateAdded,
        dateAddedBounds,
      ) + renderRangeInputs(
        'dataCoverageYear',
        t('browse.filter.dataCoverage.label'),
        t('browse.filter.dataCoverage.fromLabel'),
        t('browse.filter.dataCoverage.toLabel'),
        filterState.dataCoverageYear,
        coverageBounds,
      ),
    ))

    // Quality & availability
    const qualityChips: string[] = []
    qualityChips.push(renderBooleanChip('hasCaptions', t('browse.filter.hasCaptions.label'), hasCaptions))
    qualityChips.push(renderBooleanChip('hasStory', t('browse.filter.hasStory.label'), hasStory))
    sections.push(wrapSection(
      'quality',
      t('browse.filter.group.quality'),
      `<div class="browse-toggle-row">${qualityChips.join('')}</div>`,
    ))

    // Clear-all affordance — surfaces only when there's something
    // to clear, so the rail isn't visually noisy in the default
    // state.
    if (hasAnyActiveFilter(filterState) || searchQuery) {
      sections.push(`<div class="browse-filter-clear-wrap"><button type="button" class="browse-filter-clear" id="browse-filter-clear" aria-label="${escapeAttr(t('browse.filter.clearAll.aria'))}">${escapeHtml(t('browse.filter.clearAll'))}</button></div>`)
    }

    rail.innerHTML = sections.join('')
    syncTagCloud()
  }

  /**
   * Measure the Category tag cloud and patch its clamp in place.
   *
   * The clamp is decided from the rendered layout, not the chip
   * count, and only when it pays for itself. Clamping hides the
   * rows past the 2-row preview but adds the toggle's own line, so
   * a cloud just over two rows would come out taller clamped than
   * open. The filter group is therefore laid out both ways — clamped
   * with its toggle, open without — and the clamp is kept only when
   * it makes the group shorter by more than one chip's height.
   * Comparing the two real heights counts the toggle's margin and
   * the gaps without reading any CSS value, and follows the UI scale
   * and font. Both layouts happen before the browser paints and the
   * classes are settled right after, so nothing flickers and a
   * focused toggle keeps its focus.
   *
   * A wide panel whose chips fit in two rows, or run only a little
   * past them, gets no toggle and no fade; a narrow one with a dozen
   * long tags does. At phone widths neither the clamp nor the toggle
   * applies (accessibility.css keeps the row one sideways-scrolling
   * line), so the two heights match and the phone row is left alone.
   *
   * Skips the measurement while the row isn't laid out (panel
   * hidden, Category section collapsed); the rail's ResizeObserver
   * calls back in once it is.
   */
  function syncTagCloud(): void {
    const row = rail?.querySelector<HTMLElement>('[data-tag-cloud]')
    const toggle = rail?.querySelector<HTMLElement>('[data-tag-cloud-toggle]')
    const group = row?.parentElement
    if (!row || !toggle || !group) return
    if (row.clientWidth > 0) {
      row.classList.add('is-clamped')
      toggle.classList.remove('hidden')
      const clampedHeight = group.offsetHeight
      row.classList.remove('is-clamped')
      toggle.classList.add('hidden')
      const saved = group.offsetHeight - clampedHeight
      const chipHeight = row.querySelector<HTMLElement>('.browse-chip')?.offsetHeight ?? 0
      tagCloudWorthClamping = saved > chipHeight
    }
    row.classList.toggle('is-clamped', tagCloudWorthClamping && !chipRailExpanded)
    // Focus moving backwards into the clipped row (Shift+Tab from the
    // toggle) makes the browser scroll it to the last chip before
    // `focusin` expands the cloud, and that offset comes back with
    // the clamp. Every preview starts from the first row.
    if (row.classList.contains('is-clamped')) row.scrollTop = 0
    toggle.classList.toggle('hidden', !tagCloudWorthClamping)
    toggle.setAttribute('aria-expanded', String(chipRailExpanded))
    toggle.textContent = tagCloudToggleLabel(chipRailExpanded)
  }

  /** Expand or collapse the tag cloud in place — shared by the
   *  toggle button and the keyboard focus path, and applied without
   *  re-rendering the rail (same reason as the section headers). */
  function setTagCloudExpanded(expanded: boolean): void {
    chipRailExpanded = expanded
    syncTagCloud()
  }

  // Wire chip / toggle / range / clear listeners on the rail once.
  // The rail is re-rendered from state on every change, but the
  // delegated click handler doesn't care because the data-* attrs
  // identify the facet + value uniquely.
  if (rail && !rail.dataset.wired) {
    rail.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('#browse-filter-clear')) {
        e.preventDefault()
        applyState({}, '')
        return
      }
      // Accordion section header — toggle open/collapsed. Marked
      // before the [data-facet] match so a header click doesn't
      // also fire as a chip click (headers carry no data-facet
      // anyway, but the early-return pattern is clearer).
      const sectionHeader = target.closest('.browse-filter-section-header') as HTMLElement | null
      if (sectionHeader) {
        e.preventDefault()
        const key = sectionHeader.dataset.section as SectionKey | undefined
        if (!key) return
        const nextOpen = !sectionOpen[key]
        sectionOpen = { ...sectionOpen, [key]: nextOpen }
        saveSectionOpenState(sectionOpen)
        // Toggle the section's collapsed class + header state in
        // place rather than re-rendering the rail. The earlier
        // shape called renderRail() which rebuilt innerHTML on
        // every toggle — that thrashes the DOM and would lose any
        // partially-typed (uncommitted) range-input value if the
        // user collapsed Time before the change event fired.
        const section = sectionHeader.closest('.browse-filter-section') as HTMLElement | null
        section?.classList.toggle('collapsed', !nextOpen)
        sectionHeader.setAttribute('aria-expanded', String(nextOpen))
        const indicator = sectionHeader.querySelector('.browse-filter-section-indicator')
        if (indicator) indicator.textContent = nextOpen ? '▾' : '▸'
        // The tag cloud can't be measured while its section is
        // collapsed; measure now so it opens in the right state.
        if (key === 'category' && nextOpen) syncTagCloud()
        return
      }
      // Tag cloud "Show more / Show fewer" toggle — flips the 2-row
      // clamp in place (same pattern as the section header so the
      // rail isn't re-rendered/thrashed on toggle).
      if (target.closest('[data-tag-cloud-toggle]')) {
        e.preventDefault()
        setTagCloudExpanded(!chipRailExpanded)
        return
      }
      const chip = target.closest('[data-facet]') as HTMLElement | null
      if (!chip) return
      const facet = chip.dataset.facet
      if (!facet) return
      // Range inputs handled separately on `change` — clicks on the
      // input shouldn't toggle anything.
      if (chip.matches('input.browse-range-input')) return
      if (chip.dataset.toggle === 'boolean') {
        applyState(toggleBooleanFacet(filterState, facet), searchQuery)
        return
      }
      const value = chip.dataset.value
      if (value == null) return
      applyState(toggleFacet(filterState, facet, value), searchQuery)
    })
    rail.addEventListener('change', (e) => {
      const input = e.target as HTMLInputElement
      if (!input.matches('input.browse-range-input')) return
      const facet = input.dataset.facet
      const bound = input.dataset.bound as 'min' | 'max' | undefined
      if (!facet || !bound) return
      const raw = input.value.trim()
      const numeric = raw === '' ? undefined : Number(raw)
      if (numeric != null && !Number.isFinite(numeric)) return
      const current = filterState[facet]
      const existing = current?.kind === 'range' ? current : { kind: 'range' as const }
      const next: FacetPredicate = {
        ...existing,
        kind: 'range',
        [bound]: numeric,
      }
      const cleaned = next.min == null && next.max == null ? undefined : next
      applyState(setFacet(filterState, facet, cleaned), searchQuery)
    })
    // Keyboard focus entering the clamped tag cloud expands it.
    // The clipped chips stay in the tab order, so without this Tab
    // walks through every chip the preview doesn't show before it
    // reaches "Show more", and the browser scrolls the clipped row
    // to follow focus. Expanding on the first chip means keyboard
    // users never land on a hidden chip. Keyboard only
    // (`:focus-visible`): a mouse click that focuses a visible chip
    // shouldn't also unfold the whole cloud.
    rail.addEventListener('focusin', (e) => {
      const target = e.target as HTMLElement
      if (!target.matches('.browse-chip') || !target.closest('.browse-chip-row.is-clamped')) return
      let keyboardFocus = true
      try {
        keyboardFocus = target.matches(':focus-visible')
      } catch {
        // Engine without :focus-visible — treat every focus as keyboard.
      }
      if (keyboardFocus) setTagCloudExpanded(true)
    })
    // Re-measure the tag cloud whenever the rail's size changes: the
    // panel opening (display: none → laid out), a section opening, a
    // window resize, or a switch between the side panel and the full-
    // width catalog layout. Deferred a frame because the measurement
    // can change the rail's own height, which would re-trigger the
    // observer inside its own callback (the browser reports that as a
    // "ResizeObserver loop" error). Environments without
    // ResizeObserver (older webviews, some test harnesses) keep the
    // measurement taken at each render.
    if (typeof ResizeObserver !== 'undefined') {
      let measurePending = false
      new ResizeObserver(() => {
        if (measurePending) return
        measurePending = true
        requestAnimationFrame(() => {
          measurePending = false
          syncTagCloud()
        })
      }).observe(rail)
    }
    rail.dataset.wired = 'true'
  }

  // ----- Search input + clear button -----

  const searchInput = document.getElementById('browse-search') as HTMLInputElement | null
  const searchClear = document.getElementById('browse-search-clear')
  const updateSearchClear = () => {
    searchClear?.classList.toggle('hidden', !searchInput?.value)
  }
  /** Token guards against the async hash() call resolving for an
   * older keystroke after the user has typed more characters. */
  let searchEmitToken = 0
  let searchEmitTimer: ReturnType<typeof setTimeout> | null = null
  const scheduleSearchEmit = (raw: string) => {
    if (searchEmitTimer != null) clearTimeout(searchEmitTimer)
    if (raw.length === 0) {
      searchEmitToken++
      return
    }
    const token = ++searchEmitToken
    searchEmitTimer = setTimeout(() => {
      searchEmitTimer = null
      const cardCount = document.querySelectorAll('#browse-grid .browse-card').length
      void hashQuery(raw).then((query_hash) => {
        if (token !== searchEmitToken) return
        emit({
          event_type: 'browse_search',
          query_hash,
          query_length: raw.length,
          result_count_bucket: bucketResultCount(cardCount),
        })
      })
    }, BROWSE_SEARCH_DEBOUNCE_MS)
  }
  if (searchInput) {
    if (searchQuery && !searchInput.value) {
      // Restore the search box from URL on boot. The URL decoder
      // preserves the query's original case (see catalogFilters.ts)
      // so a shared `?q=Hurricane` link survives the round-trip
      // and a `category:Water` prefix still matches the canonical
      // `Water` tag.
      searchInput.value = searchQuery
      updateSearchClear()
    }
    if (!searchInput.dataset.wired) {
      searchInput.addEventListener('input', () => {
        const value = searchInput.value
        // Preserve case so prefix-search values match canonical
        // chip values (`category:Water` vs `Water` tag). The
        // engine's matchesSearchQuery lowercases internally for
        // the free-text path, so the case of the stored query
        // doesn't affect substring matching.
        searchQuery = value.trim()
        updateSearchClear()
        renderCards()
        // Clear-all affordance visibility depends on searchQuery
        // too — re-render the rail so the button appears/hides.
        renderRail()
        // Analytics hash continues on the lowercased form so a
        // dashboard slicing by query_hash continues to bucket
        // "Hurricane" and "hurricane" together (no regression).
        scheduleSearchEmit(searchQuery.toLowerCase())
        // AYNI: rows related in meaning, once typing pauses; the views follow the box.
        scheduleSemantic()
        refreshActiveView()
        // Persist on every keystroke so a refreshed page restores
        // the in-progress search. Cheap — applyFilterStateToUrl
        // bails out when the encoded URL hasn't changed.
        applyFilterStateToUrl(filterState, searchQuery)
      })
      searchInput.dataset.wired = 'true'
    }
    if (!callbacks.isMobile) {
      setTimeout(() => searchInput.focus(), SEARCH_FOCUS_DELAY_MS)
    }
  }
  if (searchClear && searchInput && !searchClear.dataset.wired) {
    searchClear.addEventListener('click', () => {
      searchInput.value = ''
      searchQuery = ''
      updateSearchClear()
      searchInput.focus()
      renderCards()
      renderRail()
      if (searchEmitTimer != null) clearTimeout(searchEmitTimer)
      searchEmitTimer = null
      searchEmitToken++
      refreshActiveView()
      applyFilterStateToUrl(filterState, searchQuery)
    })
    searchClear.dataset.wired = 'true'
  }

  // ----- Sort controls -----

  const sortBar = document.getElementById('browse-sort')
  const sortOptions: Array<{ key: SortKey; label: string }> = [
    { key: 'relevance', label: t('browse.sort.relevance') },
    { key: 'newest', label: t('browse.sort.newest') },
    { key: 'az', label: t('browse.sort.az') },
  ]
  if (sortBar) {
    sortBar.innerHTML = sortOptions
      .map(o => `<button class="browse-sort-btn${o.key === activeSort ? ' active' : ''}" data-sort="${o.key}" aria-pressed="${o.key === activeSort}">${o.label}</button>`)
      .join('')
    if (!sortBar.dataset.wired) {
      sortBar.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest('.browse-sort-btn') as HTMLElement | null
        if (!btn || !btn.dataset.sort) return
        activeSort = btn.dataset.sort as SortKey
        sortBar.querySelectorAll('.browse-sort-btn').forEach(b => {
          b.classList.remove('active')
          b.setAttribute('aria-pressed', 'false')
        })
        btn.classList.add('active')
        btn.setAttribute('aria-pressed', 'true')
        renderCards()
      })
      sortBar.dataset.wired = 'true'
    }
  }

  // ----- View-mode toggle (§6.7) — hidden on mobile -----

  const viewModeBar = document.getElementById('browse-view-mode')
  const graphContainer = document.getElementById('browse-graph')
  const timelineContainer = document.getElementById('browse-timeline')
  const mapContainer = document.getElementById('browse-map')
  const gridContainer = document.getElementById('browse-grid')

  /** Render the segmented Cards/Graph/Timeline/Map control. The
   *  Cards / Graph / Timeline buttons hide on narrow portrait
   *  phones (Graph + Timeline both need horizontal room the gate
   *  doesn't provide). Map stays available at every breakpoint —
   *  per §6.9 it's "the most mobile-friendly of the four
   *  view-modes" — but is rendered alongside Cards so the user can
   *  still reach the grid. The narrow-viewport branch therefore
   *  renders a two-button (Cards + Map) bar rather than going
   *  silent. */
  function renderViewModeBar(): void {
    if (!viewModeBar) return
    const narrow = isNarrowViewport()
    viewModeBar.classList.remove('hidden')
    viewModeBar.removeAttribute('aria-hidden')
    const options: Array<{ key: 'cards' | 'graph' | 'timeline' | 'map'; label: string; aria: string }> = narrow
      ? [
          { key: 'cards', label: t('browse.viewMode.cards'), aria: t('browse.viewMode.cards.aria') },
          { key: 'map', label: t('browse.viewMode.map'), aria: t('browse.viewMode.map.aria') },
        ]
      : [
          { key: 'cards', label: t('browse.viewMode.cards'), aria: t('browse.viewMode.cards.aria') },
          { key: 'graph', label: t('browse.viewMode.graph'), aria: t('browse.viewMode.graph.aria') },
          { key: 'timeline', label: t('browse.viewMode.timeline'), aria: t('browse.viewMode.timeline.aria') },
          { key: 'map', label: t('browse.viewMode.map'), aria: t('browse.viewMode.map.aria') },
        ]
    viewModeBar.innerHTML = options
      .map(o => {
        const active = o.key === viewMode
        return `<button type="button" class="browse-view-mode-btn${active ? ' active' : ''}" data-view-mode="${o.key}" aria-pressed="${active}" aria-label="${escapeAttr(o.aria)}" title="${escapeAttr(o.label)}">${VIEW_ICONS[o.key]}</button>`
      })
      .join('')
  }

  /**
   * Apply the active view-mode to the DOM — show one container,
   * hide the others. On first activation of Graph / Timeline view
   * this also lazy-imports the corresponding module, instantiates
   * the controller, and asks it to render. Subsequent activations
   * just show the existing canvas and ask the controller to
   * refresh.
   *
   * `mountIfNeeded` defaults to true so the boot path mounts the
   * graph/timeline when `viewMode` restores from localStorage.
   * Filter-change paths can pass `false` to skip the dynamic
   * import when the off-screen view isn't visible.
   */
  async function applyViewMode(mountIfNeeded: boolean = true): Promise<void> {
    if (!gridContainer || !graphContainer || !timelineContainer) return
    const hideContainer = (el: HTMLElement): void => {
      el.classList.add('hidden')
      el.setAttribute('aria-hidden', 'true')
    }
    const showContainer = (el: HTMLElement): void => {
      el.classList.remove('hidden')
      el.removeAttribute('aria-hidden')
    }
    // Body-class hook for view-aware CSS — Graph, Timeline, and
    // Map all benefit from a slimmer browse-header (less decorative
    // chrome, more canvas) while Cards keeps the welcoming title +
    // subtitle. Centralised here so every `applyViewMode` call stays
    // in sync; hideBrowseUI / collapseBrowseUI clear the classes
    // alongside `browse-open` so the body stays clean when the
    // overlay closes.
    document.body.classList.toggle('browse-view-graph', viewMode === 'graph')
    document.body.classList.toggle('browse-view-timeline', viewMode === 'timeline')
    document.body.classList.toggle('browse-view-map', viewMode === 'map')
    document.body.classList.toggle('browse-view-cards', viewMode === 'cards')
    // AYNI (2026-10-03): the filters are behind the Filters button in
    // every view now, so the drawer's state carries across a switch.
    if (viewMode === 'graph') {
      hideContainer(gridContainer)
      hideContainer(timelineContainer)
      if (mapContainer) hideContainer(mapContainer)
      showContainer(graphContainer)
      if (mountIfNeeded) await ensureGraphMounted()
      graphController?.update(viewInput())
    } else if (viewMode === 'timeline') {
      hideContainer(gridContainer)
      hideContainer(graphContainer)
      if (mapContainer) hideContainer(mapContainer)
      showContainer(timelineContainer)
      if (mountIfNeeded) await ensureTimelineMounted()
      timelineController?.update(viewInput())
    } else if (viewMode === 'map' && mapContainer) {
      hideContainer(gridContainer)
      hideContainer(graphContainer)
      hideContainer(timelineContainer)
      showContainer(mapContainer)
      if (mountIfNeeded) await ensureMapMounted()
      mapController?.update(viewInput())
    } else {
      hideContainer(graphContainer)
      hideContainer(timelineContainer)
      if (mapContainer) hideContainer(mapContainer)
      showContainer(gridContainer)
    }
  }

  /**
   * Surface a dataset's card with all its metadata expanded. Wired
   * to the Graph view's dataset-node click (see `onPreviewDataset`
   * in the `createCatalogGraph` call below) per PR #137 review
   * feedback — directly loading on graph-node click skipped the
   * description / source / time-range / keywords most users want
   * to skim first.
   *
   * Switches the view to Cards (the card UI is the established
   * metadata surface), waits for `applyViewMode` to make the grid
   * visible, finds the matching `<div class="browse-card">` by
   * data-id, expands it, scrolls it into view, and focuses it for
   * keyboard users. From there the user clicks the existing Load
   * button if they want — that still funnels through
   * `callbacks.onSelectDataset`, same path the card grid has
   * always used.
   *
   * No-ops if the dataset is missing from the filtered set (the
   * card wouldn't be in the DOM anyway); the chip rail must be
   * loose enough to surface it.
   */
  function previewDatasetInCards(datasetId: string): void {
    if (viewMode !== 'cards') {
      const previous = viewMode
      viewMode = 'cards'
      saveViewMode(viewMode)
      renderViewModeBar()
      // Emit the view-mode-change telemetry so the dashboard sees
      // graph→cards preview pivots distinctly from explicit toggle
      // clicks. Same shape as the user-clicked-the-toggle path.
      const cardCount = document.querySelectorAll('#browse-grid .browse-card').length
      emit({
        event_type: 'catalog_view_mode_changed',
        view_mode: viewMode,
        from: previous,
        result_count_bucket: bucketResultCount(cardCount),
      })
    }
    void applyViewMode().then(() => expandCardById(datasetId))
  }

  /** Find a card by its dataset id, collapse any other expanded
   *  card, expand the target, scroll it into view, and focus it.
   *  Safe to call when the card isn't in the DOM (filtered out) —
   *  silently no-ops.
   *
   *  Iterates `.browse-card` and matches on `dataset.id` rather
   *  than using a CSS attribute selector, because dataset IDs can
   *  contain characters that would need escaping inside `[data-id="..."]`
   *  (ULIDs are safe but `legacyId` strings like `INTERNAL_SOS_768`
   *  aren't always — defensive code paths in the codebase prefer
   *  property reads). */
  function expandCardById(datasetId: string): void {
    const grid = document.getElementById('browse-grid')
    if (!grid) return
    let card: HTMLElement | null = null
    const cards = grid.querySelectorAll<HTMLElement>('.browse-card')
    for (const candidate of cards) {
      if (candidate.dataset.id === datasetId) {
        card = candidate
        break
      }
    }
    if (!card) return
    grid.querySelectorAll<HTMLElement>('.browse-card.expanded').forEach(c => {
      c.classList.remove('expanded')
      c.setAttribute('aria-expanded', 'false')
    })
    card.classList.add('expanded')
    card.setAttribute('aria-expanded', 'true')
    card.scrollIntoView({ behavior: 'smooth', block: 'center' })
    card.focus()
  }

  /**
   * Lazy-import `catalogGraphUI` on first activation. Mirrors the
   * Three.js pattern in `vrSession.ts` — cytoscape is ~70 KB
   * gzipped and only users who toggle into Graph view pay that
   * cost. On import failure (network blip, bundle stripped) the
   * graph container shows an inline error and the controller stays
   * null so subsequent toggles can retry.
   */
  async function ensureGraphMounted(): Promise<void> {
    if (graphController || !graphContainer) return
    if (graphLoadPromise) {
      await graphLoadPromise
      return
    }
    graphContainer.innerHTML = `<div class="browse-graph-status" role="status">${escapeHtml(t('browse.graph.loading'))}</div>`
    const loadPromise = import('./catalogGraphUI')
      .then(({ createCatalogGraph }) => {
        graphContainer.innerHTML = ''
        graphController = createCatalogGraph(graphContainer, {
          onToggleFacet: (facet, value) => {
            applyState(toggleFacet(filterState, facet, value), searchQuery)
          },
          onPreviewDataset: (datasetId: string) => {
            previewDatasetInCards(datasetId)
          },
        })
        return graphController
      })
      .catch((err) => {
        console.error('[browse] failed to load Graph view', err)
        graphContainer.innerHTML = `<div class="browse-graph-status browse-graph-status-error" role="alert">${escapeHtml(t('browse.graph.loadError'))}</div>`
        return null
      })
    graphLoadPromise = loadPromise
    const result = await loadPromise
    // Clear the in-flight handle once it settles so a failed import
    // doesn't permanently block retry. The docstring above promises
    // "subsequent toggles can retry"; without this clear, the
    // memoised `null`-resolving promise would short-circuit forever.
    // On success the early-return at the top of `ensureGraphMounted`
    // (graphController is now set) prevents a duplicate import, so
    // clearing the handle in both branches is safe.
    if (graphLoadPromise === loadPromise) {
      graphLoadPromise = null
    }
    void result
  }

  /**
   * Lazy-import `catalogTimelineUI` on first activation. Mirrors
   * `ensureGraphMounted` — same memoised in-flight handle pattern,
   * same retry-on-error semantics. On import failure (network blip
   * in production, bundle stripped in some federated build) the
   * timeline container shows an inline error and the controller
   * stays null so a subsequent toggle into Timeline can retry.
   */
  async function ensureTimelineMounted(): Promise<void> {
    if (timelineController || !timelineContainer) return
    if (timelineLoadPromise) {
      await timelineLoadPromise
      return
    }
    timelineContainer.innerHTML = `<div class="browse-timeline-status" role="status">${escapeHtml(t('browse.timeline.loading'))}</div>`
    const loadPromise = import('./catalogTimelineUI')
      .then(({ createCatalogTimeline }) => {
        timelineContainer.innerHTML = ''
        timelineController = createCatalogTimeline(timelineContainer, {
          onBrushChange: (range) => {
            // Brush gestures funnel through `setFacet` — same single
            // mutation path the chip rail's range inputs use. The
            // resulting `applyState` re-renders the chip rail (so
            // the inputs follow the brush) AND the timeline (so the
            // brush selection re-syncs from filterState — see the
            // controller's `syncBrushFromFilterState`).
            const next = range == null
              ? setFacet(filterState, 'dataCoverageYear', undefined)
              : setFacet(filterState, 'dataCoverageYear', {
                  kind: 'range',
                  min: range.min,
                  max: range.max,
                })
            applyState(next, searchQuery)
          },
          onPreviewDataset: (datasetId: string) => {
            previewDatasetInCards(datasetId)
          },
        })
        return timelineController
      })
      .catch((err) => {
        console.error('[browse] failed to load Timeline view', err)
        timelineContainer.innerHTML = `<div class="browse-timeline-status browse-timeline-status-error" role="alert">${escapeHtml(t('browse.timeline.loadError'))}</div>`
        return null
      })
    timelineLoadPromise = loadPromise
    const result = await loadPromise
    if (timelineLoadPromise === loadPromise) {
      timelineLoadPromise = null
    }
    void result
  }

  /**
   * Lazy-import `catalogMapUI` on first activation. Mirrors
   * `ensureGraphMounted` / `ensureTimelineMounted` — same memoised
   * in-flight handle pattern, same retry-on-error semantics.
   * MapLibre is already in the bundle (the main globe imports it
   * eagerly) so the chunk cost here is the new module code only;
   * the lazy mount mostly avoids paying the second MapRenderer
   * instance's GIBS-tile warm-up until the user actually opens
   * Map view.
   */
  async function ensureMapMounted(): Promise<void> {
    if (mapController || !mapContainer) return
    if (mapLoadPromise) {
      await mapLoadPromise
      return
    }
    mapContainer.innerHTML = `<div class="browse-map-status" role="status">${escapeHtml(t('browse.map.loading'))}</div>`
    const loadPromise = import('./catalogMapUI')
      .then(({ createCatalogMap }) => {
        mapContainer.innerHTML = ''
        mapController = createCatalogMap(mapContainer, {
          onRegionChange: (bounds) => {
            // Draw gestures funnel through `setFacet` —
            // same single mutation path the chip rail's range
            // inputs and the Timeline brush both use. Clearing
            // passes undefined.
            const next = bounds == null
              ? setFacet(filterState, 'geographicRegion', undefined)
              : setFacet(filterState, 'geographicRegion', {
                  kind: 'bbox',
                  n: bounds.n,
                  s: bounds.s,
                  e: bounds.e,
                  w: bounds.w,
                })
            applyState(next, searchQuery)
          },
          onPreviewDataset: (datasetId: string) => {
            previewDatasetInCards(datasetId)
          },
        })
        return mapController
      })
      .catch((err) => {
        console.error('[browse] failed to load Map view', err)
        mapContainer.innerHTML = `<div class="browse-map-status browse-map-status-error" role="alert">${escapeHtml(t('browse.map.loadError'))}</div>`
        return null
      })
    mapLoadPromise = loadPromise
    const result = await loadPromise
    if (mapLoadPromise === loadPromise) {
      mapLoadPromise = null
    }
    void result
  }

  if (viewModeBar && !viewModeBar.dataset.wired) {
    viewModeBar.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.browse-view-mode-btn') as HTMLElement | null
      if (!btn || !btn.dataset.viewMode) return
      const next = btn.dataset.viewMode as ViewMode
      if (next === viewMode) return
      const previous = viewMode
      viewMode = next
      saveViewMode(viewMode)
      renderViewModeBar()
      const cardCount = document.querySelectorAll('#browse-grid .browse-card').length
      emit({
        event_type: 'catalog_view_mode_changed',
        view_mode: viewMode,
        from: previous,
        result_count_bucket: bucketResultCount(cardCount),
      })
      void applyViewMode()
    })
    viewModeBar.dataset.wired = 'true'
  }

  // ----- Filters drawer (small-screen, non-Cards views) -----

  const filtersBtn = document.getElementById('browse-filters-btn') as HTMLButtonElement | null
  const filtersBadge = filtersBtn?.querySelector('.browse-filters-btn-badge') as HTMLElement | null
  const filtersLabel = filtersBtn?.querySelector('.browse-filters-btn-label') as HTMLElement | null
  const activeFiltersHost = document.getElementById('browse-active-filters')

  /**
   * Render the Filters button's active-count badge AND the active-
   * filter chip strip together. Both surfaces visualise the same
   * underlying `filterState`, so a single function keeps them in
   * lockstep. Called from `applyState` on every state change; the
   * CSS handles which of them is actually visible (badge only when
   * the drawer button is shown; chip strip only when the drawer
   * is closed in drawer mode).
   *
   * Chips funnel removals through the same `toggleFacet` /
   * `toggleBooleanFacet` / `setFacet` mutations the chip rail uses,
   * so there's exactly one mutation path no matter where the user
   * clicks.
   */
  function renderActiveFilters(): void {
    const chips = collectActiveFilterChips(filterState)
    if (filtersLabel) filtersLabel.textContent = t('browse.filters.toggle.label')
    if (filtersBadge) {
      if (chips.length === 0) {
        filtersBadge.classList.add('hidden')
        filtersBadge.textContent = ''
      } else {
        filtersBadge.classList.remove('hidden')
        filtersBadge.textContent = formatNumber(chips.length)
      }
    }
    if (!activeFiltersHost) return
    if (chips.length === 0) {
      activeFiltersHost.innerHTML = ''
      activeFiltersHost.classList.add('empty')
      return
    }
    activeFiltersHost.classList.remove('empty')
    activeFiltersHost.innerHTML = chips
      .map(chip => {
        const ariaLabel = t('browse.activeFilters.remove.aria', { label: chip.label })
        return `<button type="button" class="browse-active-filter-chip" data-facet="${escapeAttr(chip.facet)}" data-kind="${escapeAttr(chip.kind)}"${chip.value != null ? ` data-value="${escapeAttr(chip.value)}"` : ''} aria-label="${escapeAttr(ariaLabel)}">${escapeHtml(chip.label)}<span class="browse-active-filter-chip-x" aria-hidden="true">×</span></button>`
      })
      .join('')
  }

  // Delegated click handler on the active-filter strip — removes the
  // facet predicate the clicked chip represents. Wired once per
  // overlay instance; the strip's HTML is re-rendered freely.
  if (activeFiltersHost && !activeFiltersHost.dataset.wired) {
    activeFiltersHost.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('.browse-active-filter-chip') as HTMLElement | null
      if (!btn) return
      const facet = btn.dataset.facet
      // AYNI: a body's list (Solar System mode) goes as one chip.
      if (facet === 'body') {
        applyState(setFacet(filterState, 'body', undefined), searchQuery)
        return
      }
      const kind = btn.dataset.kind as 'multi-select' | 'boolean' | 'range' | 'bbox' | undefined
      if (!facet || !kind) return
      if (kind === 'multi-select') {
        const value = btn.dataset.value
        if (value == null) return
        applyState(toggleFacet(filterState, facet, value), searchQuery)
      } else if (kind === 'boolean') {
        applyState(toggleBooleanFacet(filterState, facet), searchQuery)
      } else if (kind === 'range' || kind === 'bbox') {
        // Range + bbox both clear via `setFacet(..., undefined)`.
        // The Map view's draw gesture and the chip rail's range
        // inputs both write via setFacet; clearing matches.
        applyState(setFacet(filterState, facet, undefined), searchQuery)
      }
    })
    activeFiltersHost.dataset.wired = 'true'
  }

  // AYNI (owner, 2026-10-24): the Free toggle beside Filters, for free
  // accounts (and signed out): it keeps the rows the free plan opens.
  // Its own pressed look says it is on, so the strip leaves it out.
  const freeBtn = document.getElementById('browse-free-btn') as HTMLButtonElement | null

  /** Show the Free toggle to a free account only; a plan bought while it is on drops it. */
  function syncFreeButton(): void {
    if (!freeBtn) return
    const state = accountState()
    const show = state.status !== 'unknown' && currentEntitlements().plan === null
    const on = filterState.free?.kind === 'boolean'
    freeBtn.classList.toggle('hidden', !show)
    freeBtn.classList.toggle('active', on)
    freeBtn.setAttribute('aria-pressed', String(on))
    if (!show && on) applyState(setFacet(filterState, 'free', undefined), searchQuery)
  }

  if (freeBtn && !freeBtn.dataset.wired) {
    freeBtn.addEventListener('click', () => applyState(toggleBooleanFacet(filterState, 'free'), searchQuery))
    freeBtn.dataset.wired = 'true'
  }

  // Filters button wiring — toggles `.filter-drawer-open` on the
  // overlay; CSS controls the rail's positioning + visibility from
  // there. Click-outside-to-close and Escape-to-close are wired
  // once at the document level. The button's visibility itself is
  // CSS-controlled (drawer mode only); the click handler is a no-op
  // when the button isn't visible.
  if (filtersBtn && !filtersBtn.dataset.wired) {
    filtersBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      toggleFilterDrawer()
    })
    filtersBtn.dataset.wired = 'true'
  }

  function isFilterDrawerOpen(): boolean {
    return overlay?.classList.contains('filter-drawer-open') ?? false
  }

  function setFilterDrawerOpen(open: boolean): void {
    if (!overlay) return
    overlay.classList.toggle('filter-drawer-open', open)
    if (filtersBtn) filtersBtn.setAttribute('aria-expanded', String(open))
    // Anchor the drawer-positioned rail just below the toolbar.
    // `offsetTop` is relative to the offset parent (#browse-inner,
    // made `position: relative` in CSS); adding `offsetHeight` puts
    // the drawer's leading edge a hair below the toolbar with a
    // small visual gap. Recompute on each open so a re-layout (e.g.
    // search-result count changed the count line height) doesn't
    // leave the drawer floating in mid-air.
    if (open && rail) {
      const toolbar = document.getElementById('browse-toolbar')
      if (toolbar) {
        const top = toolbar.offsetTop + toolbar.offsetHeight + 8
        rail.style.setProperty('--browse-drawer-top', `${top}px`)
      }
    } else if (rail) {
      rail.style.removeProperty('--browse-drawer-top')
    }
  }

  function toggleFilterDrawer(): void {
    setFilterDrawerOpen(!isFilterDrawerOpen())
  }

  // Document-level dismissal — click outside the rail (or Escape)
  // closes the drawer. Wired once on the overlay via a dataset
  // flag so a second `showBrowseUI` call doesn't double up.
  if (overlay && !overlay.dataset.drawerWired) {
    document.addEventListener('click', (e) => {
      if (!isFilterDrawerOpen()) return
      // Ignore clicks inside the rail itself OR on the Filters
      // button (its own handler manages the toggle). The path is the
      // one the click took, so a chip the rail has just re-rendered
      // (and detached) still counts as inside — AYNI: the drawer is in
      // every view now, and a chip click must not close it.
      const path = e.composedPath()
      if (rail && path.includes(rail)) return
      if (filtersBtn && path.includes(filtersBtn)) return
      setFilterDrawerOpen(false)
    })
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isFilterDrawerOpen()) {
        setFilterDrawerOpen(false)
        filtersBtn?.focus()
      }
    })
    overlay.dataset.drawerWired = 'true'
  }

  // Live viewport listener — when the user resizes across the 768px
  // breakpoint, snap the view-mode state back to Cards if they were
  // in Graph and the viewport is now narrow. Without this the
  // overlay would show neither Cards (hidden by JS) nor Graph
  // (the toggle is gone) — a blank surface. Also re-render the
  // toggle bar so it appears / disappears symmetrically with the
  // breakpoint.
  // Type alias for the dedupe + legacy listener path.
  type NarrowVpQuery = MediaQueryList & {
    __ayniWired?: boolean
    addListener?: (cb: () => void) => void
  }
  const narrowVpQueryTyped = narrowVpQuery as NarrowVpQuery | null
  if (narrowVpQueryTyped && !narrowVpQueryTyped.__ayniWired) {
    const onNarrowChange = (): void => {
      renderViewModeBar()
      // Map view stays available at all breakpoints per §6.9 ("the
      // most mobile-friendly of the four view-modes") — only Graph
      // and Timeline snap back to Cards on narrow viewports.
      if (isNarrowViewport() && (viewMode === 'graph' || viewMode === 'timeline')) {
        viewMode = 'cards'
        saveViewMode(viewMode)
        void applyViewMode()
      }
    }
    // Newer browsers use addEventListener; older Safari falls back
    // to the deprecated addListener (iOS 14 webviews still need the
    // deprecated form).
    if (typeof narrowVpQueryTyped.addEventListener === 'function') {
      narrowVpQueryTyped.addEventListener('change', onNarrowChange)
    } else if (typeof narrowVpQueryTyped.addListener === 'function') {
      narrowVpQueryTyped.addListener(onNarrowChange)
    }
    narrowVpQueryTyped.__ayniWired = true
  }

  // ----- State application + card render -----

  /**
   * Mutate filter state + search query in one shot, then re-render
   * the rail and the card grid. Centralised so every state change
   * goes through one place (telemetry hooks here in a follow-up
   * commit; URL persistence layers on top of this too).
   */
  // AYNI: Solar System mode sets and clears the body list (setBrowseBodyScope).
  bodyScopeApplier = (values) => applyState(setFacet(filterState, 'body', values?.length ? { kind: 'multi-select', values } : undefined), searchQuery)

  function applyState(next: FilterState, nextQuery: string): void {
    const previous = filterState
    const queryChanged = nextQuery !== searchQuery
    filterState = next
    // AYNI: the headset's Browse reads the body list from here (browseBodyScope).
    const body = next.body
    bodyScopeNow = body && body.kind === 'multi-select' && body.values.length ? [...body.values] : null
    if (queryChanged) {
      searchQuery = nextQuery
      if (searchInput && searchInput.value.trim() !== nextQuery) {
        searchInput.value = nextQuery
        updateSearchClear()
      }
    }
    renderRail()
    renderActiveFilters()
    syncFreeButton()
    renderCards()
    // Graph / Timeline / Map re-render only when their canvas is
    // the active surface — no sense paying the layout cost for an
    // off-screen canvas. All three controllers' `update()` is
    // incremental: cytoscape preserves node positions across chip-
    // toggle thrashes (the §6.7 "graph thrash" risk), the timeline's
    // d3 scale + brush sync is idempotent, and the map view's
    // bbox-overlay layer swaps its GeoJSON source data in place
    // without re-mounting MapLibre.
    refreshActiveView()
    emitFilterChange(previous, next)
    // History.replaceState per §6.3 — chip clicks shouldn't clog
    // the back button, but a shared link should reproduce the
    // filter surface.
    applyFilterStateToUrl(filterState, searchQuery)
  }

  /** Redraw the Graph, Timeline or Map view, when one is showing, from what the cards show. */
  function refreshActiveView(): void {
    if (viewMode === 'graph' && graphController) {
      graphController.update(viewInput())
    } else if (viewMode === 'timeline' && timelineController) {
      timelineController.update(viewInput())
    } else if (viewMode === 'map' && mapController) {
      mapController.update(viewInput())
    }
  }

  /**
   * Emit the `browse_filter` event when a facet changes. Schema is
   * the same one PR #131 ships — `category` carries the toggled
   * value (or the facet name for boolean / range mutations) so a
   * dashboard slicing by category still works for the multi-select
   * common case. Result-count bucket reports the post-change card
   * count.
   *
   * Skips when nothing changed (avoids a redundant emit when
   * `applyState` is called with the same state — e.g. on the
   * search-query path that delegates through this helper).
   */
  function emitFilterChange(prev: FilterState, next: FilterState): void {
    const diff = facetDiff(prev, next)
    if (!diff) return
    const cardCount = document.querySelectorAll('#browse-grid .browse-card').length
    emit({
      event_type: 'browse_filter',
      category: diff,
      result_count_bucket: bucketResultCount(cardCount),
    })
  }

  // ----- AYNI: search — words first, then meaning (owner, 2026-10-03) -----

  /** Every row's words, indexed once (`smartSearch.ts`). */
  const smartIndex = buildSmartIndex(allDatasets)
  /** Meaning search (`semanticSearch.ts`); its rows come after the word matches. */
  const semantic = new SemanticSearch()
  let semanticFor: { query: string; hits: SemanticHit[] } | null = null
  let semanticTimer: ReturnType<typeof setTimeout> | null = null

  /** The licence's packages once the account is known; null until then, so no lock flickers in. */
  const lockPackages = (): ReadonlySet<string> | 'all' | null =>
    accountState().status === 'unknown' ? null : effectivePackages()

  /**
   * AYNI (owner, 2026-10-30, C9): the catalogue without the NOAA library rows
   * whose stream twin the plan plays (`libraryTwins.ts`); a free account keeps them.
   */
  const catalogue = (): Dataset[] => {
    const hidden = twinHiddenIds(allDatasets, lockPackages(), educationStreamsAllowed())
    return hidden.size ? allDatasets.filter(d => !hidden.has(d.id)) : allDatasets
  }

  /**
   * What the Catalog shows: the rows the filters allow — ranked by the
   * search when there is one — and, under them, rows related in meaning
   * that the words missed.
   */
  function computeVisible(): { rows: Dataset[]; related: Dataset[]; searching: boolean } {
    refreshVisited()
    // §6.2 — `category:foo` / `format:bar` / `period:yearly` tokens
    // AND-combine with the chips; the rest of the box is the search.
    const parsed = parseSearchQuery(searchQuery)
    const effectiveState = mergeFilterStates(filterState, parsed.prefixes)
    const allowed = filterDatasets(catalogue(), effectiveState, '', resolvers)
    const text = parsed.freeText.trim()
    const hits = text ? smartSearch(smartIndex, text) : null
    if (!hits) return { rows: allowed, related: [], searching: false }
    const allowedIds = new Set(allowed.map(d => d.id))
    const rows = hits.filter(h => allowedIds.has(h.id)).map(h => datasetById.get(h.id)!)
    const shown = new Set(rows.map(d => d.id))
    const related = semanticFor?.query === text
      ? semanticFor.hits.filter(h => allowedIds.has(h.id) && !shown.has(h.id)).map(h => datasetById.get(h.id)!)
      : []
    return { rows, related, searching: true }
  }

  /** What the Graph, Timeline and Map views draw: the same rows as the cards. */
  function viewInput(): { datasets: Dataset[]; filterState: FilterState; searchQuery: string } {
    const parsed = parseSearchQuery(searchQuery)
    const effective = mergeFilterStates(filterState, parsed.prefixes)
    if (!parsed.freeText.trim()) return { datasets: catalogue(), filterState: effective, searchQuery: '' }
    const { rows, related } = computeVisible()
    return { datasets: [...rows, ...related], filterState: effective, searchQuery: '' }
  }

  /** Ask for rows related in meaning once typing pauses. */
  function scheduleSemantic(): void {
    if (semanticTimer) clearTimeout(semanticTimer)
    semanticTimer = null
    const text = parseSearchQuery(searchQuery).freeText.trim()
    if (text.length < 3 || semantic.unavailable) return
    semanticTimer = setTimeout(() => {
      semanticTimer = null
      void semantic.prepare(allDatasets.filter(d => !d.isHidden))
      // Ranked against the whole catalogue (its spread sets the bar), then filtered like the rest.
      void semantic.rank(text, allDatasets.map(d => d.id)).then(hits => {
        if (!hits || parseSearchQuery(searchQuery).freeText.trim() !== text) return
        semanticFor = { query: text, hits }
        renderCards()
        refreshActiveView()
      })
    }, SEMANTIC_DELAY_MS)
  }

  // ----- AYNI: cards, wherever they are listed (Catalog, Recent) -----

  /** Open one card in a list (closing any other), or close it. */
  function toggleCard(container: HTMLElement, card: HTMLElement): void {
    const wasExpanded = card.classList.contains('expanded')
    container.querySelectorAll<HTMLElement>('.browse-card.expanded').forEach(c => {
      c.classList.remove('expanded')
      c.setAttribute('aria-expanded', 'false')
    })
    if (!wasExpanded) {
      card.classList.add('expanded')
      card.setAttribute('aria-expanded', 'true')
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  /** The dataset menu's "Details": open that card in the list it was chosen from. */
  function openCardIn(container: HTMLElement, datasetId: string): void {
    for (const card of container.querySelectorAll<HTMLElement>('.browse-card')) {
      if (card.dataset.id !== datasetId) continue
      if (!card.classList.contains('expanded')) toggleCard(container, card)
      card.focus()
      return
    }
  }

  /** A keyword chip: search the catalogue for it. */
  function searchForKeyword(keyword: string): void {
    if (!searchInput) return
    setBrowseTab('catalog')
    searchInput.value = keyword
    searchQuery = keyword.trim()
    updateSearchClear()
    renderCards()
    renderRail()
    scheduleSemantic()
    applyFilterStateToUrl(filterState, searchQuery)
  }

  /**
   * Wire a list of cards once, by delegation (the list is re-rendered
   * freely): Load, the "+" menu, downloads, the lock's plans link,
   * keywords, and opening a card; right-click and long press open the
   * dataset menu (`datasetMenu.ts`).
   */
  function wireCardList(container: HTMLElement): void {
    if (container.dataset.cardsWired) return
    container.dataset.cardsWired = 'true'
    const actions: DatasetMenuActions = {
      onLoad: (id) => callbacks.onSelectDataset(id),
      onDetails: (id) => openCardIn(container, id),
      announce: callbacks.announce,
      // AYNI (multi-globe plan): the dataset on a second globe, beside the one shown.
      ...(callbacks.onOpenBeside ? { onOpenBeside: (id: string) => callbacks.onOpenBeside!(id) } : {}),
    }
    attachDatasetMenu(container, (el) => el.closest<HTMLElement>('.browse-card')?.dataset.id ?? null, actions)

    container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.closest('.browse-docent-hint')) {
        callbacks.onOpenChat?.(searchQuery || undefined)
        return
      }
      const card = target.closest<HTMLElement>('.browse-card')
      if (!card) return
      const loadBtn = target.closest<HTMLElement>('.browse-card-load')
      if (loadBtn) {
        e.stopPropagation()
        const id = loadBtn.dataset.id
        if (id) callbacks.onSelectDataset(id)
        return
      }
      const menuBtn = target.closest<HTMLButtonElement>('.browse-card-menu')
      if (menuBtn) {
        e.stopPropagation()
        const id = menuBtn.dataset.datasetId
        if (id) openDatasetMenu(id, menuBtn, actions)
        return
      }
      const satBtn = target.closest<HTMLButtonElement>('.browse-card-satellite')
      if (satBtn) {
        e.stopPropagation()
        const norad = Number(satBtn.dataset.norad)
        if (Number.isFinite(norad)) callbacks.onShowSatellite?.(norad, satBtn.textContent ?? '')
        return
      }
      const lockBtn = target.closest<HTMLButtonElement>('.browse-badge-locked')
      if (lockBtn) {
        e.stopPropagation()
        openPlans(lockBtn, 'public')
        return
      }
      const zipBtnEl = target.closest<HTMLButtonElement>('.browse-card-zip')
      if (zipBtnEl) {
        e.stopPropagation()
        const id = zipBtnEl.dataset.id
        if (id) void openDownloadDialog(id, zipBtnEl)
        return
      }
      if (target.closest('a')) return
      // Clickable keywords — filter by the clicked keyword (via the search box, as before).
      const kwEl = target.closest<HTMLElement>('.browse-card-keyword')
      if (kwEl?.dataset.keyword) {
        e.stopPropagation()
        searchForKeyword(kwEl.dataset.keyword)
        return
      }
      toggleCard(container, card)
    })

    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      const target = e.target as HTMLElement
      const card = target.closest<HTMLElement>('.browse-card')
      if (!card || target.closest('button, a')) return
      const kwEl = target.closest<HTMLElement>('.browse-card-keyword')
      if (kwEl?.dataset.keyword) {
        e.preventDefault()
        e.stopPropagation()
        searchForKeyword(kwEl.dataset.keyword)
        return
      }
      e.preventDefault()
      toggleCard(container, card)
    })
  }

  // ----- AYNI: the Recent tab (was the "Continue exploring" row, §9.2) -----

  const recentHost = document.getElementById('browse-recent')

  /** Every dataset opened lately that is still in the catalogue, newest first. */
  function renderRecent(): void {
    if (!recentHost) return
    const recent = getRecent(VISITS_LRU_CAP)
      .map(id => datasetById.get(id))
      .filter((d): d is Dataset => d != null && !d.isHidden)
    if (recent.length === 0) {
      recentHost.innerHTML = `<div class="browse-recent-empty" role="status">${escapeHtml(t('browse.recent.empty'))}</div>`
      return
    }
    const packages = lockPackages()
    recentHost.innerHTML = `<div class="browse-recent-bar">`
      + `<span class="browse-recent-count">${escapeHtml(plural(recent.length, { one: 'browse.count.one', other: 'browse.count.other' }, { count: formatNumber(recent.length) }))}</span>`
      + `<button type="button" class="browse-recent-clear">${escapeHtml(t('browse.recent.clear'))}</button></div>`
      + `<div class="browse-recent-list" role="list">${recent.map(d => renderCardHtml(d, packages)).join('')}</div>`
  }

  if (recentHost && !recentHost.dataset.wired) {
    recentHost.dataset.wired = 'true'
    recentHost.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.browse-recent-clear')) return
      if (!window.confirm(t('browse.recent.clear.confirm'))) return
      clearVisits()
      renderRecent()
    })
    wireCardList(recentHost)
  }

  function sortRows(list: Dataset[]): void {
    // Relevance keeps the order given: the search's ranking, or the
    // catalogue's (weight, then title). Newest by first publication
    // (`dateAdded`), newest on top; rows without a date follow in the
    // relevance order (the sort is stable). A–Z by title.
    if (activeSort === 'newest') {
      list.sort((a, b) => {
        const ta = publishedTime(a)
        const tb = publishedTime(b)
        if (ta === tb) return 0
        if (ta === null) return 1
        if (tb === null) return -1
        return tb - ta
      })
    } else if (activeSort === 'az') {
      list.sort((a, b) => a.title.localeCompare(b.title))
    }
  }

  function renderCards(): void {
    const grid = document.getElementById('browse-grid')
    const countEl = document.getElementById('browse-count')
    if (!grid) return
    wireCardList(grid)
    const { rows, related } = computeVisible()
    sortRows(rows)
    sortRows(related)

    if (countEl) {
      const count = plural(
        rows.length,
        { one: 'browse.count.one', other: 'browse.count.other' },
        { count: formatNumber(rows.length) },
      )
      countEl.textContent = related.length > 0
        ? `${count} · ${t('browse.count.related', { count: formatNumber(related.length) })}`
        : count
    }

    if (rows.length === 0 && related.length === 0) {
      const docentHint = callbacks.onOpenChat
        ? `<button class="browse-docent-hint">${escapeHtml(t('browse.docentHint'))}</button>`
        : ''
      grid.innerHTML = `<div class="browse-no-results" role="status">${escapeHtml(t('browse.noResults'))}${docentHint ? `<br>${docentHint}` : ''}</div>`
      return
    }

    const packages = lockPackages()
    let html = rows.map(d => renderCardHtml(d, packages)).join('')
    if (related.length > 0) {
      html += `<div class="browse-related-heading" role="listitem">${escapeHtml(t('browse.related.heading'))}</div>`
      html += related.map(d => renderCardHtml(d, packages)).join('')
    }
    grid.innerHTML = html
  }

  // §9.2 — keep the visit-driven surfaces live. recordVisit fires
  // while the user is on the globe (browse collapsed/hidden); the
  // listener refreshes the Recent tab + chip in place so reopening
  // browse already reflects the latest history. The grid only needs
  // a re-render when the Recently-viewed filter is the one in play.
  onVisitsChange(() => {
    refreshVisited()
    if (activeTab === 'recent') renderRecent()
    renderRail()
    if (filterState.recentlyViewed?.kind === 'boolean') renderCards()
  })

  // AYNI (owner, 2026-10-13): and what a meeting lends while the sphere is in this participant's hands.
  onLentPackagesChange(() => {
    renderCards()
    if (activeTab === 'recent') renderRecent()
  })

  // AYNI: locks follow the account's licence (the one in use, since several, 2026-10-10).
  let inUseRef: string | null | undefined
  onAccountChange((state) => {
    syncFreeButton()
    renderCards()
    if (activeTab === 'recent') renderRecent()
    syncMyDataTab()
    // Another licence in use, another tenant: My data lists its own.
    const ref = state.status === 'signedIn' ? state.licence?.ref ?? null : null
    if (inUseRef !== undefined && inUseRef !== ref) myData?.refresh()
    if (state.status !== 'unknown') inUseRef = ref
  })

  // AYNI: the Khipus tab: a Khipu file, My Khipus, and the lessons (Education).
  const lessonsPane = document.getElementById('browse-pane-lessons')
  const lessons = lessonsPane
    ? mountLessonsTab(lessonsPane, {
      onStartLesson: (slug) => callbacks.onStartLesson?.(slug),
      onOpenKhipu: (k, files) => callbacks.onOpenKhipu?.(k, files),
      ...(callbacks.onRecordKhipu ? { onRecordKhipu: callbacks.onRecordKhipu, recordState: callbacks.recordState } : {}),
      ...(callbacks.onEditKhipu ? { onEditKhipu: callbacks.onEditKhipu } : {}),
      ...(callbacks.onNewKhipu ? { onNewKhipu: callbacks.onNewKhipu } : {}),
      onSignIn: (trigger) => openSignIn(trigger),
      onPlans: (trigger) => openPlans(trigger, 'education'),
    })
    : null

  // AYNI: the My data tab (own data).
  const myDataPane = document.getElementById('browse-pane-mydata')
  const myData = myDataPane
    ? mountMyDataTab(myDataPane, {
      onOpenDataset: (id) => callbacks.onSelectDataset(id),
      onSignIn: (trigger) => openSignIn(trigger),
      onPlans: (trigger) => openPlans(trigger, 'research', { notice: t('plans.locked.ownData') }),
    })
    : null
  syncMyDataTab()

  // AYNI: a body list asked for before Browse was built (Solar System mode).
  if (pendingBodyScope !== undefined) {
    const values = pendingBodyScope
    pendingBodyScope = undefined
    filterState = setFacet(filterState, 'body', values?.length ? { kind: 'multi-select', values } : undefined)
  }

  // Initial render
  renderRail()
  renderActiveFilters()
  syncFreeButton()
  renderCards()
  renderViewModeBar()
  wireTabs()
  // AYNI (multi-globe plan): the compatibility marks redraw the cards from outside.
  rerenderCards = () => renderCards()
  onTabShown = (tab) => {
    if (tab === 'recent') renderRecent()
    if (tab === 'lessons') lessons?.refresh()
    if (tab === 'mydata') myData?.refresh()
  }
  setBrowseTab(activeTab)
  // Restore the persisted view mode — if Graph was the last
  // surface the user had open, lazy-import on this boot path so
  // the canvas is ready when they look at it. The grid is also
  // hidden behind .hidden so we don't have a moment of layout
  // flicker.
  void applyViewMode()

  // Mark the overlay as initialized so subsequent show requests
  // can skip re-running this function and avoid duplicating
  // listeners.
  overlay.dataset.browseInitialized = 'true'
}

/** Hide the browse overlay entirely (aside becomes `display: none`). */
export function hideBrowseUI(): void {
  const overlay = document.getElementById('browse-overlay')
  overlay?.classList.add('hidden')
  document.body.classList.remove('browse-open')
  // View-aware classes get set by `applyViewMode`; clear them
  // alongside `browse-open` so the body stays clean once the
  // overlay closes.
  document.body.classList.remove('browse-view-cards', 'browse-view-graph', 'browse-view-timeline', 'browse-view-map')
  if (browseDwellHandle) {
    browseDwellHandle.stop()
    browseDwellHandle = null
  }
}

/**
 * Collapse the browse overlay while keeping it rendered so it can be
 * restored later by removing the `.collapsed` class. Use this in
 * multi-view mode where the user needs to come back to the browse
 * panel repeatedly to load datasets into additional panels.
 */
export function collapseBrowseUI(): void {
  const overlay = document.getElementById('browse-overlay')
  if (!overlay) return
  overlay.classList.remove('hidden')
  overlay.classList.add('collapsed')
  document.body.classList.remove('browse-open')
  // Mirror hideBrowseUI: drop the view-aware classes so a collapsed
  // overlay doesn't leak its last view-mode styling onto the body.
  document.body.classList.remove('browse-view-cards', 'browse-view-graph', 'browse-view-timeline', 'browse-view-map')
  if (browseDwellHandle) {
    browseDwellHandle.stop()
    browseDwellHandle = null
  }
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

/** Read the `values` array from a `multi-select` predicate as a Set
 *  for fast membership checks during chip rendering. Returns an
 *  empty Set for other predicate kinds. */
function predicateValues(predicate: FacetPredicate | undefined): ReadonlySet<string> {
  if (predicate?.kind !== 'multi-select') return new Set()
  return new Set(predicate.values)
}

/** True when any baseline facet is active. Used to decide whether
 *  to surface the "clear filters" affordance. */
function hasAnyActiveFilter(state: FilterState): boolean {
  for (const value of Object.values(state)) {
    if (value != null) return true
  }
  return false
}

/**
 * One renderable entry in the active-filter chip strip. `facet` +
 * `kind` (+ `value` for multi-select) carry the info the click
 * handler needs to remove the predicate via the same mutation
 * helpers the chip rail uses. `label` is the localised display
 * string — every variant is built here so the active-filter strip
 * never has to re-derive vocabulary.
 */
interface ActiveFilterChip {
  facet: string
  kind: 'multi-select' | 'boolean' | 'range' | 'bbox'
  value?: string
  label: string
}

/**
 * Friendly localised label for each boolean facet. Switch (not a
 * lookup table) so `t()` evaluates against the live locale at
 * chip-build time rather than at module load.
 */
function booleanFacetLabel(facet: string): string {
  switch (facet) {
    case 'hasCaptions': return t('browse.filter.hasCaptions.label')
    case 'hasStory': return t('browse.filter.hasStory.label')
    case 'recentlyViewed': return t('browse.filter.recentlyViewed.label')
    default: return facet
  }
}

function formatBucketLabel(value: string): string {
  switch (value) {
    case 'video': return t('browse.filter.format.video')
    case 'image': return t('browse.filter.format.image')
    case 'story': return t('browse.filter.format.story')
    case 'other': return t('browse.filter.format.other')
    default: return value
  }
}

function rangeFacetLabel(facet: string, predicate: FacetPredicate): string {
  if (predicate.kind !== 'range') return facet
  const start = predicate.min != null ? String(predicate.min) : '…'
  const end = predicate.max != null ? String(predicate.max) : '…'
  if (facet === 'dataCoverageYear') {
    return t('browse.activeFilters.range.dataCoverage', { start, end })
  }
  if (facet === 'dateAdded') {
    return t('browse.activeFilters.range.dateAdded', { start, end })
  }
  return `${facet} ${start}–${end}`
}

/**
 * Format a bbox predicate for the active-filter chip strip — the
 * only surface that renders the §6.9 geographicRegion predicate's
 * label today. Latitudes use N/S suffixes, longitudes E/W. Single
 * decimal of precision is enough for the chip; the underlying
 * predicate retains 3-decimal precision via the URL round-trip.
 */
function bboxFacetLabel(facet: string, predicate: FacetPredicate): string {
  if (predicate.kind !== 'bbox') return facet
  const latLabel = (v: number): string => `${Math.abs(v).toFixed(1)}°${v >= 0 ? 'N' : 'S'}`
  const lonLabel = (v: number): string => `${Math.abs(v).toFixed(1)}°${v >= 0 ? 'E' : 'W'}`
  const ns = `${latLabel(predicate.s)}–${latLabel(predicate.n)}`
  const ew = `${lonLabel(predicate.w)}–${lonLabel(predicate.e)}`
  if (facet === 'geographicRegion') {
    // AYNI: a region picked on the Map reads by its name ("Region: Spain").
    const slug = regionSlugFor(predicate)
    if (slug && (REGION_NAME_KEYS as Record<string, MessageKey | undefined>)[slug]) {
      return t('browse.activeFilters.bbox.regionNamed', { name: t(REGION_NAME_KEYS[slug as keyof typeof REGION_NAME_KEYS]) })
    }
    return t('browse.activeFilters.bbox.region', { ns, ew })
  }
  return `${facet} ${ns}, ${ew}`
}

/**
 * Walk `filterState` and emit one chip per active predicate
 * (multi-select facets contribute one chip per value so the user
 * can remove them individually — matching the chip-rail's per-
 * value interaction model).
 */
function collectActiveFilterChips(state: FilterState): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = []
  for (const [facet, predicate] of Object.entries(state)) {
    if (predicate == null) continue
    // AYNI: the Free toggle shows its own state beside Filters.
    if (facet === 'free') continue
    // AYNI: Solar System mode's body list is one chip, named by its body.
    if (facet === 'body' && predicate.kind === 'multi-select') {
      chips.push({ facet, kind: 'multi-select', value: '', label: bodyScopeLabel(predicate.values) })
      continue
    }
    if (predicate.kind === 'multi-select') {
      for (const value of predicate.values) {
        chips.push({
          facet,
          kind: 'multi-select',
          value,
          label: facet === 'format' ? formatBucketLabel(value)
            : facet === 'dataKind' && value in KIND_LABEL ? t(KIND_LABEL[value as DataKind])
              : facet === 'theme' && value in THEME_LABEL ? t(THEME_LABEL[value as Theme])
                : value,
        })
      }
    } else if (predicate.kind === 'boolean') {
      chips.push({ facet, kind: 'boolean', label: booleanFacetLabel(facet) })
    } else if (predicate.kind === 'range') {
      const hasBound = predicate.min != null || predicate.max != null
      if (!hasBound) continue
      chips.push({ facet, kind: 'range', label: rangeFacetLabel(facet, predicate) })
    } else if (predicate.kind === 'bbox') {
      // §6.9 Map view writes `geographicRegion` via setFacet. The
      // chip removes itself by clearing the predicate, same path
      // a range chip uses.
      chips.push({ facet, kind: 'bbox', label: bboxFacetLabel(facet, predicate) })
    }
  }
  return chips
}

/**
 * Single string summarising what changed between two filter
 * states — used as the `category` field on `browse_filter` events
 * so dashboards still get a stable signal per change. Returns
 * null when nothing changed.
 *
 * Format: `<facet>:<value>` for multi-select changes,
 * `<facet>:on` / `<facet>:off` for boolean toggles, `<facet>:range`
 * for range mutations.
 *
 * The compromise — packing into the existing `category` string —
 * keeps the analytics surface stable while the catalog work
 * proceeds. A schema bump that adds an explicit `facet` field is
 * the right cleanup once dashboards are ready.
 */
function facetDiff(prev: FilterState, next: FilterState): string | null {
  const facets = new Set([...Object.keys(prev), ...Object.keys(next)])
  for (const facet of facets) {
    const a = prev[facet]
    const b = next[facet]
    if (a === b) continue
    if (a == null && b != null) {
      if (b.kind === 'multi-select' && b.values.length > 0) return `${facet}:${b.values[b.values.length - 1]}`
      if (b.kind === 'boolean') return `${facet}:on`
      if (b.kind === 'range') return `${facet}:range`
    }
    if (a != null && b == null) {
      // Multi-select: removing the last value also deletes the
      // facet entirely (toggleFacet contract). Surface the
      // removed value so a category-slice dashboard still sees
      // which chip was un-toggled. Reserve `:off` for boolean
      // disappearance (where there's no value to surface).
      if (a.kind === 'multi-select' && a.values.length === 1) {
        return `${facet}:${a.values[0]}`
      }
      return `${facet}:off`
    }
    if (a?.kind === 'multi-select' && b?.kind === 'multi-select') {
      const added = b.values.find(v => !a.values.includes(v))
      if (added) return `${facet}:${added}`
      const removed = a.values.find(v => !b.values.includes(v))
      if (removed) return `${facet}:${removed}`
    }
    if (a?.kind !== b?.kind || JSON.stringify(a) !== JSON.stringify(b)) {
      return `${facet}:change`
    }
  }
  return null
}

